import { useEffect, useRef, useState } from 'react';
import DateField from '../components/DateField.jsx';
import NumBox from '../components/NumBox.jsx';
import QuickPartner from '../components/QuickPartner.jsx';
import Lookup from '../components/Lookup.jsx';
import { ServiceReview } from './ServiceConsumables.jsx';
import VoidReasonModal from '../components/VoidReasonModal.jsx';
import Suggestions, { useSuggestKeys } from '../components/Suggestions.jsx';
import GroupPicker from '../components/GroupPicker.jsx';
import QuickPeriod from '../components/QuickPeriod.jsx';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { PaymentMethodModal } from './DocumentView.jsx';
import { zadarzhiProdazhba } from '../App.jsx';
import {
  api, withQuery, money, quantity, unitPrice, date, DOC_TYPE_LABEL, STATUS_LABEL, STATUS_CLASS, PAYMENT_LABEL,
} from '../api.js';
import { usePaymentMethods } from '../hooks.js';

/**
 * Въвеждане на продажба или доставка.
 *
 * Цената се въвежда БЕЗ ДДС и в двата случая — така се работи в къщата.
 * Картонът на стоката държи нетни цени, такива са и фактурите на
 * доставчиците, които операторът препечатва ред по ред. Данъкът се
 * начислява отгоре и се вижда в сбора отдолу.
 *
 * Продажбата дълго време искаше цена С ДДС, но полето се пълнеше с
 * нетната цена от картона. Стоката излизаше с една пета по-евтина и
 * нищо не го показваше: числото в полето изглеждаше редно.
 */
export default function NewDocument() {
  const { docType: docTypeParam, id: continueId, editId } = useParams();
  const docType = (continueId || editId) ? 'sale' : docTypeParam;
  /*
   * Редакция на записана продажба без касова бележка (02.10.2026).
   *
   * Същият екран като новата продажба — наличности, партиди, цени от
   * предишни продажби, тонер/чип, подаръци — по думите на Калин „абсолютно
   * същия начин“. Разликите: няма чернова и УНП (документът вече ги има),
   * клиентът/складът/плащането не се сменят оттук, а „Запис“ минава през
   * PATCH /sale-items — сървърът връща и взема стоката по разликата.
   */
  const [editDoc, setEditDoc] = useState(null);
  const [smenyaPlashtane, setSmenyaPlashtane] = useState(false);
  const [smenyaKlient, setSmenyaKlient] = useState(false);
  const navigate = useNavigate();
  // Откъдето е отворена продажбата (Чакат продажба, Стари продажби) — „Затвори“
  // в прегледа след записа връща там (03.11.2026). Без него — Продажба/Доставка.
  const otkade = useLocation().state?.otSpisak ?? null;
  const kamPregled = otkade ? { state: { otSpisak: otkade } } : undefined;

  /*
   * Кой от двата стари списъка се показва вместо формата, ако изобщо —
   * бутоните за тях стоят трайно горе, само сменят какво има отдолу.
   *
   * Живее в адреса (?old=…), не в локално състояние: иначе бутонът
   * "Назад" на браузера, след отваряне на стар документ от списъка,
   * връщаше на празната форма вместо на самия списък — адресът се
   * пазеше в историята непроменен, а локалното състояние не.
   */
  const [searchParams] = useSearchParams();
  const oldView = searchParams.get('old');

  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [partner, setPartner] = useState(null);
  const [partnerQuery, setPartnerQuery] = useState('');
  const [partnerHits, setPartnerHits] = useState([]);
  const [addingPartner, setAddingPartner] = useState(false);
  const [editingPartner, setEditingPartner] = useState(false);
  const [novaStoka, setNovaStoka] = useState(false);
  // Продажба на случаен купувач „на каса" — картонът „РАЗНИ ООД?" вместо
  // издирване на контрагент, който така или иначе не се знае.
  const [razni, setRazni] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [priceLevel, setPriceLevel] = useState('retail');
  // Заявката към myPOS терминала, докато чакаме клиентът да плати —
  // виж payWithMyPos(). null означава, че в момента не се чака никой.
  const [myposPayment, setMyposPayment] = useState(null);
  /*
   * Терминалът не поема заявката (02.10.2026): тя е в облака на myPOS,
   * но апаратът е заспал или POSLink Manager не е отпред. След 15 сек.
   * без отговор това се казва направо, вместо безкрайно „Чака…“.
   */
  const [myposBavi, setMyposBavi] = useState(false);
  useEffect(() => {
    if (myposPayment?.status !== 'InProgress') { setMyposBavi(false); return undefined; }
    const t = setTimeout(() => setMyposBavi(true), 15000);
    return () => clearTimeout(t);
  }, [myposPayment?.status, myposPayment?.id]);
  const myposCancelRef = useRef(false);
  /*
   * Какво знае картонът за плащането на този клиент.
   *
   * Нужно е, за да се разбере дали избраното от оператора се различава от
   * уговореното — и да му се предложи да го запише, вместо да се разминават
   * мълчаливо всеки път.
   */
  const [payHint, setPayHint] = useState(null);
  const [payAsk, setPayAsk] = useState(null);
  // Очакваният номер на фактурата — само за поглед, не е запазен.
  const [nextInvoice, setNextInvoice] = useState(null);
  // Очакваният номер на продажбата преди първата стока — също само за поглед.
  const [nextDocNo, setNextDocNo] = useState(null);
  // Бележката от последния продаден ред — предлага се на следващия.
  const [poslednaBelezhka, setPoslednaBelezhka] = useState('');
  /*
   * Започнатата продажба: номерът ѝ в базата и нейният УНП.
   *
   * По Приложение № 29, т. 9.1 продажбата е „въведена" в мига, в който
   * първата стока влезе в екранната форма — тогава ѝ се дава УНП. Затова
   * първият продаден ред отваря чернова на сървъра; останалите се въвеждат
   * на екрана и лягат при „Запис".
   */
  const [draft, setDraft] = useState(null);
  const [nomerKopiran, setNomerKopiran] = useState(false);
  const [issueInvoice, setIssueInvoice] = useState(false);
  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState('');
  // Фактурата почти винаги е от днес; когато не е, се сменя от календара.
  // sv-SE дава точно ГГГГ-ММ-ДД, което полето type=date иска.
  const [supplierInvoiceDate, setSupplierInvoiceDate] = useState(
    () => new Date().toLocaleDateString("sv-SE")
  );
  const [note, setNote] = useState('');
  // Транспорт за наша сметка и отстъпка за цялата фактура — две
  // отделни числа, и двете положителни. Един доставчик прави и двете.
  const [transportCost, setTransportCost] = useState('');
  const [invoiceDiscount, setInvoiceDiscount] = useState('');
  // Ръчният разход е за единични случаи — фиктивна фактура, вложено в
  // ремонт. Затова колоната стои скрита, докато не потрябва.
  const [showCost, setShowCost] = useState(false);
  const [lines, setLines] = useState([]);
  const { methods, byCode } = usePaymentMethods();
  const [productQuery, setProductQuery] = useState('');
  const [productHits, setProductHits] = useState([]);
  const [pokaziHits, setPokaziHits] = useState(true);
  const [productTotal, setProductTotal] = useState(0);
  const [samoNalichni, setSamoNalichni] = useState(true);
  /*
   * Стоката, която се разглежда преди да влезе в продажбата.
   *
   * Досега редът лягаше в документа и чак тогава се виждаше от коя партида
   * излиза и на каква цена сме продавали — решението се вземаше след като
   * е взето. Тук се разглежда първо и се слага наготово.
   */
  const [gled, setGled] = useState(null);
  const [credit, setCredit] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const partnerInput = useRef(null);
  const productInput = useRef(null);
  const invoiceInput = useRef(null);
  const [reading, setReading] = useState(false);
  // Записът за разчитането: пази файла и помни връзките към стоките.
  const [imported, setImported] = useState(null);
  // Довършване на чужда (по-точно: чакаща) продажба — зарежда се от сървъра
  // вместо да тръгва от празно.
  const [hydrating, setHydrating] = useState(!!(continueId || editId));

  const isPurchase = docType === 'purchase';
  const partnerRole = isPurchase ? 'supplier' : 'client';

  /*
   * Смяна на таба Продажба/Доставка е НОВ документ, не продължение.
   *
   * Компонентата не се размонтира при навигацията между `/documents/new/
   * sale` и `/documents/new/purchase` — React Router само сменя
   * параметъра. Без изричното чистене тук контрагентът, редовете и
   * отвореният панел от предишния таб оставаха в паметта и изтичаха в
   * другия: доставка от Х се доотваряше с редовете и дори с отворения
   * панел „продавам" от предишна продажба на СЪЩИЯ екран. Веднъж видяно
   * на живо (30.08.2026) — недопустимо, дори само визуално.
   */
  useEffect(() => {
    if (continueId || editId) return;
    setPartner(null);
    setPartnerQuery('');
    setPartnerHits([]);
    setRazni(false);
    setCredit(null);
    setLines([]);
    setGled(null);
    setDraft(null);
    setPaymentMethod('cash');
    setProductQuery('');
    setProductHits([]);
    setIssueInvoice(false);
    setSupplierInvoiceNo('');
    setTransportCost('');
    setInvoiceDiscount('');
    setNote('');
    setImported(null);
    setError(null);
    setPoslednaBelezhka('');
    partnerInput.current?.focus();
  }, [docType, continueId, editId]);

  /*
   * Довършване на прекъсната продажба.
   *
   * Приложение № 29, т. 9.1: УНП е даден при първата стока и продажбата
   * може да стои отворена — токът спира, операторът се разсейва. Тук се
   * зарежда съществуващата чернова (склад, клиент, начин на плащане,
   * редове) в същия екран, по който тръгва нова продажба: „Запис" минава
   * през същия `draft`, значи довършва СЪЩИЯ документ, не създава нов.
   */
  useEffect(() => {
    const zaredi = continueId ?? editId;
    if (!zaredi) return;
    let cancelled = false;
    (async () => {
      try {
        const doc = await api.get(`/api/documents/${zaredi}`);
        if (cancelled) return;
        if (editId && (doc.doc_type !== 'sale' || doc.status !== 'confirmed')) {
          setError('Само записана продажба се редактира оттук.');
          setHydrating(false);
          return;
        }
        if (continueId && (doc.doc_type !== 'sale' || doc.status !== 'draft')) {
          setError('Само незавършена продажба може да се довърши оттук.');
          setHydrating(false);
          return;
        }

        setWarehouseId(String(doc.warehouse_id));
        setPartner(doc.partner_id ? { id: doc.partner_id, name: doc.partner_name } : null);
        setPartnerQuery(doc.partner_name ?? '');
        setPaymentMethod(doc.payment_method);
        setNote(doc.note ?? '');
        if (editId) {
          setEditDoc({
            id: doc.id, docNumber: doc.doc_number, unp: doc.unp, invoiceNo: doc.invoice_no,
            warehouseName: doc.warehouse_name, partnerName: doc.partner_name,
            paymentMethod: doc.payment_method,
            dbIds: (doc.items ?? []).filter((it) => !Number(it.is_cancelled)).map((it) => it.id),
          });
        } else {
          setDraft({ id: doc.id, unp: doc.unp, docNumber: doc.doc_number });
        }

        const redove = (doc.items ?? []).filter((it) => !Number(it.is_cancelled));
        const built = await Promise.all(redove.map(async (it) => {
          let available = 0;
          let isStocked = true;
          if (it.product_id) {
            try {
              const info = await api.get(withQuery(`/api/products/${it.product_id}/sale-info`, {
                warehouseId: doc.warehouse_id,
              }));
              isStocked = !!info.product?.is_stocked;
              available = (info.stock ?? []).reduce((a, r) => a
                + (Number(r.warehouse_id) === Number(doc.warehouse_id) ? Number(r.quantity) : 0), 0);
              // Записаната продажба вече е взела своето от склада — то се
              // връща при поправка, значи е налично за същия ред.
              if (editId) available += Number(it.quantity);
            } catch { /* стоката може да е сменена междувременно — редът остава както е бил */ }
          }
          return {
            key: `existing-${it.id}`,
            dbId: editId ? it.id : undefined,
            origQty: editId ? Number(it.quantity) : undefined,
            clientKey: it.client_key || `existing-${it.id}`,
            productId: it.product_id,
            description: it.description,
            unit: it.unit,
            vatRate: Number(it.vat_rate),
            available,
            isStocked,
            quantity: String(it.quantity),
            price: String(it.price),
            batchId: it.batch_id ?? '',
            batches: null,
            isGift: !!it.is_gift,
            note: it.note ?? null,
          };
        }));
        if (!cancelled) {
          setLines(built);
          // Продажба, тръгнала от Деливери без предварително избран клиент
          // (обектът още не е свързан с фирма) — маркерът отива на полето
          // „Клиент", не на стоката.
          if (doc.partner_id) productInput.current?.focus();
          else partnerInput.current?.focus();
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [continueId, editId]);
  useEffect(() => {
    if (isPurchase || !issueInvoice) { setNextInvoice(null); return; }
    api.get('/api/documents/next-invoice-no')
      .then((r) => setNextInvoice(r.expected))
      .catch(() => setNextInvoice(null));
  }, [issueInvoice, isPurchase]);
  // Опреснява се на половин минута — другото работно място също започва
  // продажби и номерът се мести.
  const chakaNomer = !isPurchase && !editId && !continueId && !draft?.id;
  useEffect(() => {
    if (!chakaNomer) { setNextDocNo(null); return undefined; }
    const vzemi = () => api.get('/api/documents/next-doc-no')
      .then((r) => setNextDocNo(r.expected))
      .catch(() => setNextDocNo(null));
    vzemi();
    const t = setInterval(vzemi, 30_000);
    return () => clearInterval(t);
  }, [chakaNomer]);

  const chosenMethod = byCode[paymentMethod];

  // Каквото накрая ляга върху себестойността: транспортът я вдига,
  // отстъпката я сваля. Показва се, за да не се смята наум.
  const extraNet = Math.round(
    ((Math.abs(Number(transportCost) || 0)) - (Math.abs(Number(invoiceDiscount) || 0))) * 100
  ) / 100;

  /*
   * Продажба тръгва само от склад с признак „продава" — при нас само
   * Варна. Трябва ли нещо от друг склад, то се прехвърля във Варна и се
   * продава оттам. Доставка се приема във всеки склад.
   *
   * Сървърът пази същото правило; тук списъкът е скъсен, за да не се
   * избира нещо, което после ще бъде отказано.
   */
  useEffect(() => {
    api.get('/api/warehouses')
      .then((list) => {
        const w = isPurchase ? list : list.filter((x) => x.can_sell);
        setWarehouses(w);
        setWarehouseId(String(w[0]?.id ?? ''));
      })
      .catch((e) => setError(e.message));
  }, [isPurchase]);

  useEffect(() => {
    if (partnerQuery.trim().length < 2) { setPartnerHits([]); return; }
    // След избор полето показва името на избрания и търсенето по него би
    // отворило списъка пак — с една-единствена възможност, същата.
    if (partner && partnerQuery === partner.name) { setPartnerHits([]); return; }

    const timer = setTimeout(() => {
      api.get(withQuery('/api/partners', { q: partnerQuery, type: partnerRole, limit: 40 }))
        .then((r) => setPartnerHits(r.items))
        .catch(() => setPartnerHits([]));
    }, 220);
    return () => clearTimeout(timer);
  }, [partnerQuery, partnerRole, partner]);

  useEffect(() => {
    if (productQuery.trim().length < 2) { setProductHits([]); return; }
    const timer = setTimeout(() => {
      api.get(withQuery('/api/products', {
        q: productQuery.trim(), warehouseId: warehouseId || undefined,
        // Капачката от осем режеше редовно: бърз код „УФР" хваща 40 стоки,
        // „к310" — 19. Списъкът скролва, а броят се изписва.
        limit: 200,
        inStock: (!isPurchase && samoNalichni) || undefined,
        // Услуга не се доставя от никого — виж `excludeServices`.
        excludeServices: isPurchase || undefined,
        // Цената в списъка да е същата, която ще влезе в полето.
        withLastPurchase: isPurchase || undefined,
        partnerId: (isPurchase && partner?.id) || undefined,
      }))
        .then((r) => { setProductHits(r.items); setProductTotal(Number(r.total ?? r.items.length)); })
        .catch(() => { setProductHits([]); setProductTotal(0); });
    }, 220);
    return () => clearTimeout(timer);
  }, [productQuery, warehouseId, isPurchase, partner?.id, samoNalichni]);

  /** Избраният контрагент носи своите настройки за плащане и ценово ниво. */
  function choosePartner(p) {
    setPartner(p);
    setPartnerQuery(p.name);
    setPartnerHits([]);
    // Избор от списъка също включва/изключва чекбокса — той просто отразява
    // дали избраният е точно картонът „РАЗНИ ООД?", по който и да е пътят.
    setRazni(p.name === 'РАЗНИ ООД?');
    // Клиентът е избран — следващата стъпка е стоката.
    productInput.current?.focus(); productInput.current?.select();
    /*
     * Начинът се пита от сървъра: уговореното в картона, иначе последната
     * продажба с този клиент, иначе „по банков път".
     *
     * Банката е безопасната: не издава бележка и не закрива задължението,
     * тоест забравена смяна оставя вземане за следене. При „в брой" щеше
     * да е пуснат бон за пари, които никой не е получил, а това се
     * поправя само със сторно.
     */
    setPayHint(null);
    setPayAsk(null);
    if (!isPurchase) {
      api.get(`/api/partners/${p.id}/payment-method`)
        .then((h) => {
          setPayHint(h);
          setPaymentMethod(h.suggested);
          /*
           * „Издай фактура" не се отмята сама по предишната продажба
           * (решение на Калин, 30.09.2026) — операторът я слага нарочно.
           * Иначе фактура излизаше и там, където клиентът не я иска, и
           * вземаше номер от поредицата.
           */
          setIssueInvoice(false);
        })
        .catch(() => setPaymentMethod(p.default_payment_method || 'bank'));
    } else if (p.default_payment_method) {
      setPaymentMethod(p.default_payment_method);
    }
    if (!isPurchase && p.default_price_level) setPriceLevel(p.default_price_level);

    // Просрочията се проверяват веднага, а не чак при провеждането —
    // няма смисъл да се въвежда цяла продажба, която ще бъде отказана.
    setCredit(null);
    if (!isPurchase) {
      api.get(`/api/partners/${p.id}/credit`).then(setCredit).catch(() => {});
    }
  }

  /** Чекбоксът „Разни" избира готовия картон вместо ръчно търсене. */
  async function toggleRazni(checked) {
    setRazni(checked);
    if (!checked) {
      setPartner(null);
      setPartnerQuery('');
      return;
    }
    try {
      const r = await api.get(withQuery('/api/partners', { q: 'РАЗНИ ООД', type: 'client', limit: 5 }));
      const p = r.items.find((x) => x.name === 'РАЗНИ ООД?') ?? r.items[0];
      if (p) choosePartner(p); else setRazni(false);
    } catch {
      setRazni(false);
    }
  }

  /**
   * Качва фактура на доставчика и изсипва разчетеното в екрана.
   *
   * Няма междинен списък: файлът се разчита и веднага става доставка за
   * довършване. Записът за разчитането остава на сървъра — там се пази
   * файлът и оттам се помнят връзките „код на доставчика -> наша стока“.
   *
   * Редове, за които стока не е разпозната, влизат в списъка празни и
   * чакат да бъдат посочени. Посочи ли се веднъж, се помни.
   */
  async function loadInvoice(file) {
    if (!file) return;
    setReading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const res = await fetch('/api/invoice-imports', {
        method: 'POST', credentials: 'same-origin', body: form,
      });
      const inv = await res.json().catch(() => null);
      if (!res.ok) throw new Error(inv?.error ?? `Грешка ${res.status}`);

      setImported(inv);
      if (inv.invoice_no) setSupplierInvoiceNo(inv.invoice_no);
      // Ред „отстъпка −4.00" по цялата фактура: разчита се отделно и
      // попълва полето, вместо да влиза като стока.
      if (Number(inv.transport_cost) || Number(inv.invoice_discount)) {
        if (Number(inv.transport_cost)) setTransportCost(String(Number(inv.transport_cost)));
        if (Number(inv.invoice_discount)) setInvoiceDiscount(String(Number(inv.invoice_discount)));
      }
      if (inv.invoice_date) setSupplierInvoiceDate(String(inv.invoice_date).slice(0, 10));

      if (inv.partner_id) {
        const p = await api.get(`/api/partners/${inv.partner_id}`);
        choosePartner(p);
      } else {
        setPartner(null);
        setPartnerQuery('');
      }

      setLines(inv.lines.map((l) => ({
        key: `imp-${l.id}`,
        importLineId: l.id,
        productId: l.product_id ?? null,
        description: l.product_name ?? l.description ?? '(без наименование)',
        productNote: l.product_note ?? null,
        productGroup: l.product_group ?? null,
        productProperties: l.product_properties ?? null,
        supplierText: [l.supplier_code, l.description].filter(Boolean).join(' · '),
        matchKind: l.match_kind,
        unit: l.product_unit ?? 'бр.',
        vatRate: l.vat_rate != null ? Number(l.vat_rate) : 20,
        available: 0,
        quantity: String(Number(l.quantity ?? 0)),
        // Фактурите на доставчиците са без ДДС, а тук цената се въвежда
        // с ДДС — затова се начислява.
        // Цената остава каквато я пише доставчикът — без ДДС. Преди
        // тук се умножаваше по ставката и в колоната излизаше число,
        // каквото на фактурата го няма.
        price: l.unit_price != null
          ? Number(l.unit_price).toFixed(4)
          : '0.00',
      })));
    } catch (e) {
      setError(e.message);
    } finally {
      setReading(false);
      if (invoiceInput.current) invoiceInput.current.value = '';
    }
  }

  /**
   * Потвърждаване на налучкана връзка.
   *
   * Разчитането я е познало по прилика, но това е предположение, докато
   * човек не го погледне. Потвърждаването не мени стоката — то казва, че
   * е гледана, редът посинява и „по прилика" отпада.
   *
   * Запомня се като ръчно посочена, за да не се налучква пак: следващата
   * фактура от този доставчик ще я намери направо.
   */
  function confirmLine(line) {
    patchLine(line.key, { matchKind: 'manual' });
    if (imported?.id && line.importLineId && line.productId) {
      api.put(`/api/invoice-imports/${imported.id}/lines/${line.importLineId}`,
        { productId: line.productId, remember: true }).catch(() => {});
    }
  }

  /** Посочена стока за неразпознат ред — запомня се за следващия път. */
  async function linkImportLine(line, product) {
    patchLine(line.key, {
      productId: product.id,
      description: product.name,
      productNote: product.note ?? null,
      productGroup: product.group_name ?? null,
      productProperties: product.properties_text ?? null,
      unit: product.unit,
      vatRate: Number(product.vat_rate ?? line.vatRate),
      matchKind: 'manual',
      picking: false,
    });
    if (imported?.id && line.importLineId) {
      api.put(`/api/invoice-imports/${imported.id}/lines/${line.importLineId}`,
        { productId: product.id, remember: true }).catch(() => {});
    }
    /*
     * При продажба редът без цена (несвързан ред от Деливери) взима цената,
     * която екранът би предложил за тази стока и този клиент (28.10.2026).
     */
    if (!isPurchase && !(Number(line.price) > 0)) {
      try {
        const info = await api.get(withQuery(`/api/products/${product.id}/sale-info`, {
          warehouseId: warehouseId || undefined, partnerId: partner?.id || undefined, priceLevel,
        }));
        if (info.prices?.suggested != null) {
          patchLine(line.key, { price: cenaVPole(info.prices.suggested) });
        }
      } catch { /* цената остава за ръчно */ }
    }
  }

  /**
   * Партидите на стоката в текущия склад, с наличност.
   *
   * Продажбата тръгва от най-старата партида, освен ако не се посочи
   * друга. При тънка надценка това решава дали сделката е на печалба:
   * една и съща касета може да лежи в две партиди с чувствително различна
   * доставна цена, и продажбата от скъпата излиза на загуба, без нищо да
   * го покаже.
   */
  async function chetiPartidi(productId) {
    try {
      const r = await api.get(`/api/products/${productId}/batches`);
      if (!r.product?.is_batch_tracked) return [];
      return (r.batches ?? [])
        .filter((b) => Number(b.warehouse_id) === Number(warehouseId) && Number(b.quantity) > 0)
        .map((b) => ({
          id: b.id,
          batchNo: b.batch_no,
          quantity: Number(b.quantity),
          price: b.purchase_price == null ? null : Number(b.purchase_price),
          date: b.created_at,
        }));
    } catch { return []; }
  }

  /**
   * Отваряне на панела за разглеждане.
   *
   * Едно и също за продажба и доставка: количество и цена се решават
   * тук, срещу наличните партиди и историята на цените, преди редът да
   * влезе в документа. При доставка партидите се показват само за
   * сведение — редът винаги отваря своя нова партида, не се избира от
   * коя да излезе (обратното на продажбата).
   */
  // Стрелки и Enter в двата падащи списъка (09.10.2026).
  const klaviaturaStoka = useSuggestKeys(productHits, razgledaj, (p) => p.group_name,
    productHits.length > 0 && pokaziHits);
  const klaviaturaKlient = useSuggestKeys(partnerHits, choosePartner);

  /*
   * Чакат продажба за този клиент (22.10.2026) — изписани в Деливери ремонти
   * и зареждания. Директната продажба ги показва над редовете с „Добави“;
   * при запис добавените се свързват с продажбата, за да не останат в
   * „Чакат продажба“ (Дон Кихот, Байкушева… бяха продадени на ръка).
   */
  const [chakashti, setChakashti] = useState([]);
  useEffect(() => {
    if (isPurchase || editId || !partner?.id) { setChakashti([]); return undefined; }
    let alive = true;
    api.get(withQuery('/api/chakat-prodazhba/za-klient', { partnerId: partner.id }))
      .then((r) => { if (alive) setChakashti(r.items ?? []); })
      .catch(() => { if (alive) setChakashti([]); });
    return () => { alive = false; };
  }, [isPurchase, editId, partner?.id]);
  const dobaveniRefs = new Set(lines.map((l) => l.serviceRef).filter(Boolean));

  async function dobaviUsluga(u) {
    if (!u.productId) return;
    await razgledaj({ id: u.productId, name: u.predmet });
    setGled((g) => (g && g.id === u.productId ? {
      ...g,
      price: u.bezDds == null ? g.price : cenaVPole(u.bezDds),
      note: u.belezhka,
      serviceRef: u.ref,
      // Сменените части (барабан, нож, доктор нож…) — отделни редове услуга.
      chasti: u.chasti ?? [],
    } : g));
  }

  async function razgledaj(p) {
    /*
     * Търсенето остава (07.10.2026): често се продава по една от всеки
     * цвят — след „Продай“ същият списък се връща, а добавените носят
     * „+1 в продажбата“. Скрива се само докато е отворен панелът.
     */
    setPokaziHits(false);
    setGled({ id: p.id, name: p.name, loading: true });

    if (isPurchase) {
      try {
        const [info, histRes] = await Promise.all([
          api.get(`/api/products/${p.id}/batches`),
          api.get(withQuery('/api/products/last-purchases', {
            productIds: String(p.id), partnerId: partner?.id || undefined,
          })),
        ]);
        const hist = histRes.items?.[p.id] ?? null;
        const price = p.last_purchase_price ?? hist?.same?.price ?? hist?.other?.price
          ?? p.purchase_price;
        setGled({
          id: p.id,
          name: p.name,
          loading: false,
          isPurchase: true,
          product: p,
          info,
          hist,
          warehouseId,
          quantity: '1',
          price: price != null ? Number(price).toFixed(2) : '',
          priceGross: null,
          note: poslednaBelezhka,
        });
      } catch (e) {
        setError(e.message);
        setGled(null);
      }
      return;
    }

    try {
      const [info, consumables] = await Promise.all([
        api.get(withQuery(`/api/products/${p.id}/sale-info`, {
          warehouseId: warehouseId || undefined,
          partnerId: partner?.id || undefined,
          priceLevel,
        })),
        // Услуга „Зареждане" с вече закачен тонер/чип — виж „Тонер/чип на
        // услугите". За всичко останало връща празни списъци, безобидно.
        api.get(withQuery(`/api/service-consumables/${p.id}/for-sale`, {
          warehouseId: warehouseId ? Number(warehouseId) : undefined,
        })).catch(() => ({ toners: [], chips: [] })),
      ]);
      setGled({
        id: p.id,
        name: info.product.name,
        loading: false,
        info,
        warehouseId,
        priceLevel,
        methodName: Object.fromEntries(methods.map((m) => [m.code, m.name])),
        quantity: '1',
        price: info.prices.suggested == null ? '' : cenaVPole(info.prices.suggested),
        batchId: '',
        priceGross: null,
        note: poslednaBelezhka,
        consumables,
        chosenTonerId: consumables.toners[0]?.productId ?? null,
        // По подразбиране „без чип“ (01.10.2026) — чипът се сменя рядко и
        // операторът го отмята сам, когато наистина е сменен.
        chosenChipId: null,
      });
    } catch (e) {
      setError(e.message);
      setGled(null);
    }
  }

  /**
   * Изоставената продажба се анулира.
   *
   * По т. 12 продажба с УНП не подлежи на изтриване — остава като запис с
   * причина. Затова напускането на екрана с започната продажба пита, а не
   * мълчи.
   */
  async function anuliraj(prichina) {
    if (!draft) return;
    try {
      await api.post(`/api/documents/${draft.id}/void`, { reason: prichina });
      setVoiding(false);
      if (continueId) { navigate(`/documents/${draft.id}`, kamPregled); return; }
      setDraft(null);
      setLines([]);
    } catch (e) { setError(e.message); }
  }

  /** Редът, както го въведе операторът — за екрана и за сървъра. */
  function redZaZapis(l) {
    return {
      // Ключът пътува със сървъра: по него той различава поправен ред от
      // махнат и добавен наново — първото е промяна, второто анулиране.
      // При „Довърши" редът носи ключа, с който е записан първия път —
      // иначе сървърът би го анулирал и добавил наново при всеки запис.
      clientKey: l.clientKey ?? l.key,
      productId: l.productId,
      description: l.description,
      unit: l.unit,
      quantity: Number(l.quantity || 0),
      price: l.isGift ? 0 : Number(l.price || 0),
      vatRate: l.vatRate,
      isGift: !!l.isGift,
      batchId: l.batchId ? Number(l.batchId) : undefined,
      note: l.note || undefined,
      unitCost: String(l.unitCost ?? '').trim() === '' ? undefined : Number(l.unitCost),
    };
  }

  /**
   * Първата стока открива продажбата.
   *
   * Тук се дава УНП — не при избора на клиент и не при „Запис". Ако
   * откриването не мине, редът не влиза в екрана: по-добре нищо, отколкото
   * продажба на екрана без номер зад нея.
   */
  async function otvoriProdazhba(parviRed) {
    const res = await api.post('/api/documents', {
      docType: 'sale',
      warehouseId: Number(warehouseId),
      partnerId: partner?.id ?? null,
      status: 'draft',
      paymentMethod,
      pricesNet: true,
      items: [redZaZapis(parviRed)],
    });
    setDraft({ id: res.id, unp: res.unp, docNumber: res.docNumber });
    return res;
  }

  /** Разгледаното влиза в документа като готов ред. */
  async function prodaj() {
    if (!gled?.info) return;
    const info = gled.info;
    const obshtoNaSklad = info.stock.reduce((a, r) => a
      + (Number(r.warehouse_id) === Number(warehouseId) ? Number(r.quantity) : 0), 0);

    const obshtoOsnova = {
      productId: gled.id,
      description: info.product.name,
      unit: info.product.unit,
      vatRate: Number(info.product.vat_rate),
      isStocked: !!info.product.is_stocked,
      // Отметка „Подарък" в панела (20.10.2026) — редът влиза направо на 0.
      isGift: !!gled.isGift,
      // От коя чакаща услуга е (22.10.2026) — свързва се при записа.
      serviceRef: gled.serviceRef ?? null,
      price: gled.isGift ? '0' : String(gled.price || '0'),
      batches: info.batches,
      saleHist: { client: info.prices.client, any: info.prices.any },
      cardPrice: info.prices.card,
      // Описанието пътува с реда — иначе трябва да се пита наново.
      productCode: info.product.code,
      properties: info.product.properties_text,
      productNote2: info.product.note,
      note: (gled.note ?? '').trim() || null,
    };

    /*
     * Кликнато по партиди — толкова редове, колкото партиди са пипнати.
     *
     * Всеки ред носи собствената партида и точно взетото от нея; цената е
     * една и съща за всички, операторът я е решил веднъж за цялата стока.
     */
    let razbivka = Object.entries(gled.batchQty ?? {}).filter(([, n]) => n > 0);
    const ostatak = ostatakPoPartidi(info.batches, lines, gled.id);
    /*
     * Без избрана партида, но стоката вече е на екрана от някоя партида:
     * „по реда на постъпване" се разписва тук, по останалото — иначе
     * сървърът пак ще посегне на вече изчерпаната (09.10.2026).
     */
    const pipnato = lines.some((l) => Number(l.productId) === Number(gled.id)
      && (l.dbId ? Number(l.quantity) !== Number(l.origQty ?? l.quantity) : Number(l.quantity) > 0));
    if (razbivka.length === 0 && pipnato && ostatak.length > 0) {
      let ostava = Number(gled.quantity || 1);
      razbivka = [];
      for (const b of ostatak) {
        if (ostava <= 0) break;
        const t = Math.min(ostava, b.quantity);
        razbivka.push([String(b.id), t]);
        ostava -= t;
      }
      if (ostava > 0 && razbivka.length) razbivka[razbivka.length - 1][1] += ostava;
    }
    const redove = razbivka.length > 0
      ? razbivka.map(([batchId, kol], i) => {
        const b = ostatak.find((x) => String(x.id) === String(batchId))
          ?? info.batches.find((x) => String(x.id) === String(batchId));
        return {
          ...obshtoOsnova,
          key: `${gled.id}-${Date.now()}-${i}`,
          available: b ? Number(b.quantity) : obshtoNaSklad,
          quantity: String(kol),
          batchId,
        };
      })
      : [{
        ...obshtoOsnova,
        key: `${gled.id}-${Date.now()}`,
        available: obshtoNaSklad,
        quantity: String(gled.quantity || '1'),
        batchId: gled.batchId || '',
      }];

    /*
     * Избраният тонер/чип за услугата (виж „Тонер/чип на услугите") влиза
     * като отделен ред, подарък — изписва се от склада, без пари за
     * клиента, но носи себестойност от партидата си (т. 4 в CLAUDE.md).
     * Пълно sale-info за консуматива, не само име/мярка — иначе редът не
     * може после да се редактира или да покаже партида като всеки друг.
     */
    const chosen = [
      gled.consumables?.toners.find((t) => t.productId === gled.chosenTonerId),
      gled.consumables?.chips.find((c) => c.productId === gled.chosenChipId),
    ].filter(Boolean);

    const giftRedove = [];
    for (const cons of chosen) {
      try {
        const cinfo = await api.get(withQuery(`/api/products/${cons.productId}/sale-info`, {
          warehouseId: warehouseId || undefined, partnerId: partner?.id || undefined, priceLevel,
        }));
        const naSklad = cinfo.stock.reduce((a, r) => a
          + (Number(r.warehouse_id) === Number(warehouseId) ? Number(r.quantity) : 0), 0);
        giftRedove.push({
          productId: cons.productId,
          description: cinfo.product.name,
          unit: cinfo.product.unit,
          vatRate: Number(cinfo.product.vat_rate),
          isStocked: !!cinfo.product.is_stocked,
          price: '0',
          isGift: true,
          batches: cinfo.batches,
          saleHist: { client: cinfo.prices.client, any: cinfo.prices.any },
          cardPrice: cinfo.prices.card,
          productCode: cinfo.product.code,
          properties: cinfo.product.properties_text,
          productNote2: cinfo.product.note,
          note: null,
          key: `${cons.productId}-${Date.now()}-gift`,
          available: naSklad,
          quantity: cons.quantityUnits != null
            ? String(Number(cons.quantityUnits.toFixed(4))) : '1',
          batchId: '',
        });
      } catch {
        // Стоката липсва/спряна — редът просто не се добавя, операторът
        // го вижда и решава ръчно, вместо продажбата да пропадне заради него.
      }
    }

    // Сменените при зареждането части (от „Чакат продажба“, 23.10.2026).
    const chastRedove = [];
    for (const ch of gled.chasti ?? []) {
      try {
        const cinfo = await api.get(withQuery(`/api/products/${ch.productId}/sale-info`, {
          warehouseId: warehouseId || undefined, partnerId: partner?.id || undefined, priceLevel,
        }));
        chastRedove.push({
          productId: ch.productId,
          description: cinfo.product.name,
          unit: cinfo.product.unit,
          vatRate: Number(cinfo.product.vat_rate),
          isStocked: !!cinfo.product.is_stocked,
          price: ch.bezDds == null ? '' : cenaVPole(ch.bezDds),
          batches: cinfo.batches,
          saleHist: { client: cinfo.prices.client, any: cinfo.prices.any },
          cardPrice: cinfo.prices.card,
          productCode: cinfo.product.code,
          properties: cinfo.product.properties_text,
          productNote2: cinfo.product.note,
          note: (gled.note ?? '').trim() || null, // с/н на касетата
          key: `${ch.productId}-${Date.now()}-chast`,
          available: 0,
          quantity: '1',
          batchId: '',
        });
      } catch {
        // липсва услугата — операторът я добавя на ръка
      }
    }

    if (!draft && !editId) {
      try {
        await otvoriProdazhba(redove[0]);
      } catch (e) {
        setError(e.message);
        return;
      }
    }

    setLines((ls) => [...ls, ...redove, ...chastRedove, ...giftRedove]);
    /*
     * Бележката остава за следващата стока.
     *
     * Обикновено цялата доставка е за същия отдел и преписването ѝ на
     * всеки ред е излишна работа. Изтрива се с едно избърсване.
     */
    setPoslednaBelezhka((gled.note ?? '').trim());
    setGled(null);
    productInput.current?.focus(); productInput.current?.select();
  }

  /** Разгледаната доставка влиза в документа като готов ред. */
  function dobaviDostavka() {
    if (!gled?.product) return;
    const p = gled.product;
    setLines((ls) => [...ls, {
      key: `${gled.id}-${Date.now()}`,
      productId: gled.id,
      description: p.name,
      unit: p.unit,
      vatRate: Number(p.vat_rate),
      available: Number(p.quantity ?? 0),
      quantity: String(gled.quantity || '1'),
      price: gled.price !== '' ? Number(gled.price).toFixed(2) : '0.00',
      batchId: '',
      batches: null,
      saleHist: null,
      cardPrice: null,
      note: (gled.note ?? '').trim() || null,
    }]);
    setPoslednaBelezhka((gled.note ?? '').trim());
    setGled(null);
    productInput.current?.focus(); productInput.current?.select();
  }

  /*
   * Смяна на начина от оператора.
   *
   * Ако избраното се разминава с картона, се пита дали да се запише там.
   * Подканата е ред под полето, не прозорец пред него: операторът въвежда
   * продажба и не бива да го спираме, а изборът му отнема едно натискане
   * и в двете посоки.
   */
  function smeniNachin(code) {
    setPaymentMethod(code);
    // „РАЗНИ ООД?“ — случаен купувач, няма какво да се записва в картона.
    if (isPurchase || !partner || !payHint || payHint.source === 'razni') return;
    if (code === payHint.fixed) { setPayAsk(null); return; }
    setPayAsk({ code, hadFixed: !!payHint.fixed });
  }

  async function zapishiVKarton(code) {
    try {
      await api.patch(`/api/partners/${partner.id}`, { defaultPaymentMethod: code });
      setPayHint((h) => ({ ...h, fixed: code }));
      setPayAsk(null);
    } catch (e) { setError(e.message); }
  }

  function patchLine(key, patch) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  /*
   * Въведеното е основата; данъкът се начислява отгоре. Смятането следва
   * въведеното, за да съвпада с фактурата до стотинка.
   */
  const byRate = new Map();                   // ставка -> основа
  const totals = lines.reduce((acc, l) => {
    if (l.isGift) return acc;                 // подаръкът не влиза в сумата
    const line = round2(Number(l.quantity || 0) * Number(l.price || 0));
    const rate = (l.vatRate || 0) / 100;
    const net = line;
    const gross = round2(line * (1 + rate));
    byRate.set(l.vatRate || 0, round2((byRate.get(l.vatRate || 0) ?? 0) + net));
    acc.gross += gross;
    acc.net += net;
    return acc;
  }, { gross: 0, net: 0 });
  totals.vat = round2(totals.gross - totals.net);

  /*
   * Отстъпката за цялата фактура е РЕД от нея: сваля основата, а данъкът
   * се смята наново върху свалената — точно както го смята доставчикът.
   * Транспортът „за наша сметка" не влиза тук; той вдига себестойността,
   * но не и дължимото.
   */
  // Името на избрания начин — за подканата и за съобщенията.
  const imeNa = (code) => methods.find((m) => m.code === code)?.name ?? code;

  const cut = isPurchase ? Math.abs(Number(invoiceDiscount) || 0) : 0;
  if (cut > 0 && totals.net > 0) {
    const base = totals.net;
    const take = Math.min(cut, base);
    const groups = [...byRate.entries()];
    let left = take;
    let net = 0;
    let vat = 0;
    for (const [i, [rate, groupNet]] of groups.entries()) {
      const share = i === groups.length - 1 ? left : round2((take * groupNet) / base);
      left = round2(left - share);
      const groupBase = round2(groupNet - share);
      net = round2(net + groupBase);
      vat = round2(vat + round2((groupBase * rate) / 100));
    }
    totals.net = net;
    totals.vat = vat;
    totals.gross = round2(net + vat);
  }

  /**
   * Праща сумата на документа към myPOS терминала и чака резултат.
   *
   * Извиква се между записа на редовете и „Запис" — документът вече има
   * УНП, но нищо необратимо не се е случило, затова е безопасно да се
   * спре тук, ако плащането пропадне. Хвърля, ако не завърши с успех, за
   * да не се стигне до провеждане на документа без взети пари.
   */
  async function payWithMyPos(documentId) {
    myposCancelRef.current = false;
    let p = await api.post('/api/mypos/payments', { documentId });
    setMyposPayment(p);

    while (p.status === 'InProgress') {
      if (myposCancelRef.current) {
        // Сървърът казва какво е станало наистина — може терминалът вече
        // да е приел картата (07.10.2026, 0000040639).
        p = await api.post(`/api/mypos/payments/${p.id}/cancel`)
          .catch(() => ({ ...p, status: 'Canceled' }));
        break;
      }
      await new Promise((r) => setTimeout(r, 2000));
      p = await api.get(`/api/mypos/payments/${p.id}`);
      setMyposPayment(p);
    }
    setMyposPayment(p);

    if (p.status !== 'Success') {
      throw new Error(
        p.status === 'Canceled' ? 'Плащането през терминала е отказано.'
          : p.status === 'Rejected' ? 'Картата е отказана от терминала.'
            : `Плащането не приключи успешно (${p.status}).`
      );
    }

    // Кратка пауза, за да успее операторът да види потвърждението, преди
    // екранът да прескочи към записания документ.
    await new Promise((r) => setTimeout(r, 1200));
  }

  /*
   * Започнатата продажба се пази на сървъра сама (01.10.2026).
   *
   * Досега редовете след първия живееха само в браузъра до „Запис" —
   * Ctrl+F5, затворен раздел или срив губеха цялата въведена продажба
   * (на 01.10 така изчезнаха 7 реда за МБАЛ „Света Анна"). Сега около
   * секунда след всяка промяна редовете отиват в черновата. Махнат ред
   * остава там анулиран — както иска Приложение № 29, т. 12.
   *
   * Не се праща същото два пъти, и не се праща, докато тече „Запис".
   */
  const posledno = useRef('');
  useEffect(() => {
    if (!draft || isPurchase || busy || lines.length === 0) return undefined;
    if (lines.some((l) => !l.productId)) return undefined;
    const telo = JSON.stringify(podredeni(lines).map(redZaZapis));
    if (telo === posledno.current) return undefined;
    const t = setTimeout(() => {
      api.put(`/api/documents/${draft.id}/lines`, { pricesNet: true, items: JSON.parse(telo) })
        .then(() => { posledno.current = telo; })
        .catch(() => { /* при „Запис" ще се опита пак и грешката ще излезе там */ });
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, draft, busy, isPurchase]);

  /*
   * Колко от стоката вече стои на екрана, но още не е излязло от склада.
   *
   * Складът намалява чак при „Запис“ — дотогава търсенето показваше
   * пълната наличност, сякаш добавеното го няма (02.10.2026). При
   * редакция записаните редове вече са взели своето: брои се само
   * разликата спрямо записаното количество.
   */
  function zaetoNaEkrana(productId) {
    return lines.reduce((a, l) => {
      if (Number(l.productId) !== Number(productId) || l.isStocked === false) return a;
      const kol = Number(l.quantity) || 0;
      return a + (l.dbId ? kol - Number(l.origQty ?? kol) : kol);
    }, 0);
  }

  // Редовете на екрана при редакция — за „Запис“ и преди смяна на плащането.
  function redoveZaRedakcia() {
    const ostavat = new Set(lines.map((l) => l.dbId).filter(Boolean));
    return [
      ...podredeni(lines).map((l) => (l.dbId
        ? {
          id: l.dbId,
          quantity: Number(l.quantity || 0),
          price: l.isGift ? 0 : Number(l.price || 0),
          note: l.note || null,
        }
        : {
          productId: l.productId,
          description: l.description,
          unit: l.unit,
          quantity: Number(l.quantity || 0),
          price: l.isGift ? 0 : Number(l.price || 0),
          vatRate: l.vatRate,
          isGift: !!l.isGift,
          batchId: l.batchId ? Number(l.batchId) : null,
          note: l.note || null,
        })),
      // Махнатите редове остават в документа анулирани (т. 12).
      ...editDoc.dbIds.filter((id) => !ostavat.has(id))
        .map((id) => ({ id, isCancelled: true })),
    ];
  }

  async function save(status) {
    setBusy(true);
    setError(null);
    try {
      if (editDoc) {
        await api.patch(`/api/documents/${editDoc.id}/sale-items`, { items: redoveZaRedakcia() });
        navigate(`/documents/${editDoc.id}`, kamPregled);
        return;
      }
      /*
       * Започнатата продажба се довършва, а не се създава наново.
       *
       * Тя вече има УНП от първата стока. Нов документ тук би дал втори
       * номер, а първият би останал да виси като изоставена продажба.
       */
      if (draft) {
        await api.put(`/api/documents/${draft.id}/lines`, {
          pricesNet: true,
          items: podredeni(lines).map(redZaZapis),
        });
        if (!isPurchase && paymentMethod === 'card') {
          await payWithMyPos(draft.id);
        }
        const res = await api.post(`/api/documents/${draft.id}/confirm`, {
          paymentMethod,
          issueInvoice,
          note: note || null,
          // Клиентът може да се е сменил, след като продажбата е започнала.
          partnerId: partner?.id ?? null,
        });
        setDraft(null);
        // Добавените от „Чакат продажба“ услуги — свързват се с продажбата.
        const refs = [...new Set(lines.filter((l) => l.serviceRef).map((l) => l.serviceRef))];
        if (refs.length) {
          await api.post('/api/chakat-prodazhba/svarzhi', { refs, documentId: res.id ?? draft.id })
            .catch(() => {});
        }
        zadarzhiProdazhba(res.id ?? draft.id);
        navigate(`/documents/${res.id ?? draft.id}`, kamPregled);
        return;
      }

      const res = await api.post('/api/documents', {
        docType,
        warehouseId: Number(warehouseId),
        partnerId: partner?.id ?? null,
        status,
        paymentMethod,
        issueInvoice: isPurchase ? false : issueInvoice,
        invoiceNo: isPurchase ? (supplierInvoiceNo.trim() || null) : undefined,
        invoiceDate: isPurchase ? (supplierInvoiceDate || null) : undefined,
        note: note || null,
        // Цените по редовете са без ДДС — и при доставка, и при продажба.
        pricesNet: true,
        transportCost: isPurchase && transportCost.trim() !== ''
          ? Math.abs(Number(transportCost)) : undefined,
        invoiceDiscount: isPurchase && invoiceDiscount.trim() !== ''
          ? Math.abs(Number(invoiceDiscount)) : undefined,
        items: podredeni(lines).map((l) => ({
          productId: l.productId,
          description: l.description,
          unit: l.unit,
          quantity: Number(l.quantity || 0),
          price: l.isGift ? 0 : Number(l.price || 0),
          vatRate: l.vatRate,
          isGift: !!l.isGift,
          // Празно значи „по реда на постъпване“ — системата разнася сама.
          batchId: l.batchId ? Number(l.batchId) : undefined,
          // Бележката за реда излиза на фактурата.
          note: l.note || undefined,
          // Празно значи „системата да я сметне“ — от партидата, от
          // която излиза стоката. Попълва се само когато разходът се
          // знае единствено от човека.
          unitCost: String(l.unitCost ?? '').trim() === '' ? undefined : Number(l.unitCost),
        })),
      });
      if (!isPurchase) zadarzhiProdazhba(res.id);
      navigate(`/documents/${res.id}`, kamPregled);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      setMyposPayment(null);
    }
  }

  // Ред без посочена стока не се записва — иначе доставката влиза в
  // склада без да се знае какво е влязло.
  const unlinked = lines.filter((l) => !l.productId).length;
  /*
   * Наличността се проверява ПРЕДИ записа, не се чака сървърна грешка.
   *
   * Сървърът и без това отказва отрицателна наличност — базата го пази с
   * тригер. Но операторът не бива да стига до "Запис" само за да получи
   * груба грешка: жълтият бадж "налично Х" вече го показва на реда,
   * копчето трябва да откаже да работи заедно с него. Услуга (is_stocked
   * = false) няма склад и не се брои.
   */
  const nadNalichnost = !isPurchase && lines.some((l) =>
    l.isStocked !== false && Number(l.quantity) > Number(l.available));
  const ready = lines.length > 0 && warehouseId && unlinked === 0 && !nadNalichnost
    && lines.every((l) => Number(l.quantity) > 0);

  /*
   * Продажбата се пази като недовършена на сървъра ("Довърши" я връща).
   * Доставката не — редовете ѝ живеят само на екрана до "Запис". Затова
   * при смяна на таба с въведени редове предупреждаваме различно.
   */
  function switchDocType(next) {
    if (next === docType && !oldView) return;
    if (next !== docType && lines.length > 0) {
      const soobshtenie = isPurchase
        ? 'Има въведени редове за доставка, които не са записани. Да продължа ли?'
        : 'Продажбата е с въведени редове — ще остане недовършена и ще можете да я довършите после. Да продължа ли?';
      if (!window.confirm(soobshtenie)) return;
    }
    navigate(`/documents/new/${next}`);
  }

  if (hydrating) return <div className="empty">Зареждане на продажбата…</div>;

  if ((continueId && error && !draft) || (editId && error && !editDoc)) {
    return (
      <>
        <div className="error">{error}</div>
        <Link to={`/documents/${continueId ?? editId}`} className="button">Обратно към документа</Link>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <h1>
          {oldView
            ? (oldView === 'purchase' ? 'Стари доставки' : oldView === 'drafts' ? 'Чернови продажби' : 'Стари продажби')
            : editId ? `Редакция на продажба № ${editDoc?.docNumber ?? ''}`
              : (continueId ? 'Довършване на продажба' : (DOC_TYPE_LABEL[docType] ?? 'Нов документ'))}
        </h1>
        <div className="spacer" />
        {/* Разчитането на фактура е начин да се въведе доставка, затова
            стои тук, а не като отделен раздел в менюто. */}
        {!oldView && isPurchase && (
          <>
            <input ref={invoiceInput} type="file" style={{ display: 'none' }}
              accept=".pdf,.rtf,.png,.jpg,.jpeg,.tif,.tiff,.webp"
              onChange={(e) => loadInvoice(e.target.files?.[0])} />
            <button className="primary" disabled={reading}
              onClick={() => invoiceInput.current?.click()}>
              {reading ? 'Разчитане…' : 'Качи фактура'}
            </button>
          </>
        )}
      </div>

      {!continueId && !editId && (
        <div className="tabs">
          <button className={`tab ${!oldView && !isPurchase ? 'active' : ''}`}
            onClick={() => switchDocType('sale')}>
            Продажба
          </button>
          <button className={`tab ${!oldView && isPurchase ? 'active' : ''}`}
            onClick={() => switchDocType('purchase')}>
            Доставка
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className={`tab ${oldView === 'drafts' ? 'active' : ''}`}
            onClick={() => navigate(`/documents/new/${docType}?old=drafts`)}>
            Чернови
          </button>
          <button type="button" className={`tab ${oldView === 'sale' ? 'active' : ''}`}
            onClick={() => navigate(`/documents/new/${docType}?old=sale`)}>
            Стари продажби
          </button>
          <button type="button" className={`tab ${oldView === 'purchase' ? 'active' : ''}`}
            onClick={() => navigate(`/documents/new/${docType}?old=purchase`)}>
            Стари доставки
          </button>
        </div>
      )}

      {oldView === 'drafts' ? (
        <OldDocuments type="sale" initialStatus="draft" />
      ) : oldView ? (
        <OldDocuments type={oldView} />
      ) : (
      <>

      {error && <div className="error">{error}</div>}

      {credit?.hasOverdue && (
        <div className={credit.blocked ? 'error' : 'warning'}>
          <strong>
            {credit.blocked
              ? `Продажбите към „${credit.partnerName}" са спрени. `
              : 'Клиентът има просрочено задължение. '}
          </strong>
          {money(credit.overdueAmount)} при срок {credit.creditDays} дни
          {credit.usesDefaultDays ? ' (по подразбиране)' : ''}.
          {credit.documents.length > 0 && (
            <> Най-старото: <Link to={`/documents/${credit.documents[0].id}`}>
              {credit.documents[0].docNumber}
            </Link> — просрочено с {credit.documents[0].daysLate} дни.</>
          )}
          {credit.blocked && ' Продажбата няма да се проведе, докато задължението не бъде платено.'}
        </div>
      )}

      <div className="card">
        <div className="card-body">
          {/*
            * Открита продажба: номерът ѝ вече е даден и не се връща.
            *
            * Показва се, защото оттук нататък всяко напускане на екрана
            * оставя следа — и защото същият номер ще излезе на бона.
            */}
          {chakaNomer && nextDocNo && (
            <div style={{
              display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap',
            }}>
              <strong style={{ fontSize: 16 }}>Продажба № {nextDocNo}</strong>
              <span className="muted" style={{ fontSize: 11 }}>
                (очакван — потвърждава се с първата стока)
              </span>
            </div>
          )}
          {draft?.unp && (
            <div style={{
              display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8,
              flexWrap: 'wrap',
            }}>
              {/* Номерът на продажбата — някои клиенти го искат в бележката
                  на реда; копира се с един клик. */}
              {draft.docNumber && (
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <strong style={{ fontSize: 16 }}>Продажба № {draft.docNumber}</strong>
                  <button type="button" className="link" title="Копирай номера"
                    onClick={() => {
                      navigator.clipboard?.writeText(draft.docNumber).then(
                        () => setNomerKopiran(true), () => {});
                      setTimeout(() => setNomerKopiran(false), 1500);
                    }}>
                    {nomerKopiran ? 'копирано ✓' : 'копирай'}
                  </button>
                </span>
              )}
              <span className="badge">УНП {draft.unp}</span>
              <span className="muted" style={{ fontSize: 11 }}>
                номерът е даден при първата стока и остава за цялата продажба
              </span>
              <button type="button" className="link" onClick={() => setVoiding(true)}>
                анулирай започнатата
              </button>
            </div>
          )}

          {editDoc && (
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <div className="field">Склад<div className="field-static">{editDoc.warehouseName ?? '—'}</div></div>
              <div className="field" style={{ minWidth: 220 }}>
                Клиент
                {smenyaKlient ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Lookup
                      url="/api/partners"
                      value={null}
                      label=""
                      placeholder="търсене по име или ЕИК"
                      onPick={async (id) => {
                        if (!id) return;
                        try {
                          const r = await api.patch(`/api/documents/${editDoc.id}/partner`, { partnerId: id });
                          if (!r.unchanged) {
                            setEditDoc((d) => ({ ...d, partnerName: r.partnerName }));
                            setPartner({ id: r.partnerId, name: r.partnerName });
                            setPartnerQuery(r.partnerName);
                          }
                          setSmenyaKlient(false);
                        } catch (e) { setError(e.message); }
                      }}
                    />
                    <button type="button" className="link" onClick={() => setSmenyaKlient(false)}>откажи</button>
                  </div>
                ) : (
                  <div className="field-static">
                    {editDoc.partnerName ?? 'на каса'}
                    {' '}
                    <button type="button" className="link" onClick={() => setSmenyaKlient(true)}>смени</button>
                  </div>
                )}
              </div>
              <div className="field">Плащане<div className="field-static">
                {methods.find((m) => m.code === editDoc.paymentMethod)?.name ?? editDoc.paymentMethod}
                {' '}
                <button type="button" className="link" onClick={() => setSmenyaPlashtane(true)}>смени</button>
              </div></div>
              {smenyaPlashtane && (
                <PaymentMethodModal
                  doc={{
                    id: editDoc.id,
                    payment_method: editDoc.paymentMethod,
                    payment_method_name: methods.find((m) => m.code === editDoc.paymentMethod)?.name,
                  }}
                  methods={methods}
                  onClose={() => setSmenyaPlashtane(false)}
                  /* Бонът при „В брой“ излиза за записаната сума. Добавеното на
                     екрана се губеше и бонът не съвпадаше с исканото (Гатев,
                     03.11.2026) — затова редовете се записват преди смяната. */
                  beforeChange={async () => {
                    if (!ready) throw new Error('Първо оправи редовете — има ред без стока или над наличното.');
                    await api.patch(`/api/documents/${editDoc.id}/sale-items`, { items: redoveZaRedakcia() });
                  }}
                  // След смяната продажбата може вече да е с бон (в брой) —
                  // тогава не се редактира; връщаме се в документа.
                  onChanged={async () => navigate(`/documents/${editDoc.id}`, kamPregled)}
                />
              )}
              {editDoc.invoiceNo && (
                <div className="field">
                  Фактура
                  <div className="field-static">
                    {editDoc.invoiceNo}
                    {' '}
                    {/* Отметката „издай фактура" сложена по погрешка (13.10.2026):
                        последната, днешна, непечатана фактура връща номера си. */}
                    <button type="button" className="link" onClick={async () => {
                      if (!window.confirm(
                        `Да махна фактура № ${editDoc.invoiceNo} от продажбата?

`
                        + 'Само ако НЕ е печатана и не е дадена на клиента. Номерът се връща '
                        + 'и следващата фактура ще го вземе. Продажбата остава с протокол.')) return;
                      try {
                        await api.post(`/api/documents/${editDoc.id}/withdraw-invoice`, { notPrinted: true });
                        setEditDoc((d) => ({ ...d, invoiceNo: null }));
                      } catch (e) { setError(e.message); }
                    }}>махни</button>
                  </div>
                </div>
              )}
              <div className="field">УНП<div className="field-static">{editDoc.unp ?? '—'}</div></div>
              <Link to={`/documents/${editDoc.id}`} className="link" style={{ marginLeft: 'auto' }}>
                откажи редакцията
              </Link>
            </div>
          )}
          {/* Какво следва от избрания начин — над лентата, за да не крепи
              колоната и да не криви подравняването на реда. */}
          {!editDoc && !isPurchase && chosenMethod && (
            <div className="hint" style={{ marginBottom: 8 }}>
              {chosenMethod.issuesFiscalReceipt ? 'с касова бележка' : 'без касова бележка'}
              {chosenMethod.settlesImmediately
                ? ' · задължението се закрива веднага'
                : ' · задължението остава да се следи'}
              {payHint?.source === 'last' && paymentMethod === payHint.suggested
                && ' · начинът е както при последната продажба'}
              {payHint?.source === 'default' && paymentMethod === payHint.suggested
                && ' · няма предишна продажба с този клиент'}
            </div>
          )}
          {isPurchase && paymentMethod === 'cash' && (
            <div className="hint" style={{ marginBottom: 8 }}>
              ще се отбележи платена веднага
            </div>
          )}
          <div className="toolbar" style={{ marginBottom: 0, display: editDoc ? 'none' : undefined }}>
            {/* Един-единствен склад не е избор — показва се, не се пита. */}
            {warehouses.length === 1 ? (
              <div className="field">
                Склад
                <div className="field-static">{warehouses[0].name}</div>
              </div>
            ) : (
              <label className="field">
                Склад
                <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                  {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </label>
            )}

            <div className="field" style={{ position: 'relative', minWidth: 260 }}>
              {/* „Разни" стои плътно до „Клиент", не в другия край на полето. */}
              <label style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span>{isPurchase ? 'Доставчик' : 'Клиент'}</span>
                {/* Продажба „на каса" за случаен купувач, но без да остане
                    „на каса" (без контрагент): вместо да го търси, операторът
                    отбелязва тук и се избира готовият картон „РАЗНИ ООД?". */}
                {!isPurchase && (
                  <span style={{ display: 'flex', gap: 4, alignItems: 'center', fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={razni}
                      onChange={(e) => toggleRazni(e.target.checked)}
                    />
                    Разни
                  </span>
                )}
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  ref={partnerInput}
                  className="tarsachka"
                  type="search"
                  autoComplete="off"
                  style={{ flex: 1 }}
                  placeholder={partner ? partner.name : 'търсене по име или ЕИК — празно значи на каса'}
                  value={partnerQuery}
                  onChange={(e) => { setPartnerQuery(e.target.value); setPartner(null); setRazni(false); }}
                  onKeyDown={klaviaturaKlient.onKeyDown}
                />
                <button type="button" title={`Нов ${isPurchase ? 'доставчик' : 'клиент'}`}
                  onClick={() => setAddingPartner(true)}>
                  + нов
                </button>
                {/* Бърза редакция на избраната фирма (05.10.2026) — без
                    да се напуска продажбата. */}
                {partner && (
                  <button type="button" title={`Редакция на ${partner.name}`}
                    onClick={() => setEditingPartner(true)}>
                    редакция
                  </button>
                )}
              </div>
              {partnerHits.length > 0 && (
                <Suggestions
                  activeId={klaviaturaKlient.activeId}
                  items={partnerHits}
                  render={(p) => <>{p.name} <span className="muted">{p.eik ?? ''}</span></>}
                  onPick={choosePartner}
                />
              )}
            </div>

            {/*
              * Подсказката стои НАД лентата, а не под полето.
              *
              * Лентата подравнява по долния ръб; текст под едно от полетата
              * прави колоната по-висока и вдига етикета ѝ над останалите.
              * Оттам идваше разкривеният ред.
              */}
            <label className="field">
              Плащане
              <select value={paymentMethod} onChange={(e) => smeniNachin(e.target.value)}>
                {methods.map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
              </select>
            </label>

            {!isPurchase && (
              <label className="field">
                Цени
                <select value={priceLevel} onChange={(e) => setPriceLevel(e.target.value)}>
                  <option value="retail">дребно</option>
                  <option value="wholesale">едро</option>
                </select>
              </label>
            )}

            {isPurchase ? (
              <>
                {/* При доставка фактурата е чужда — преписва се от нея,
                    не се издава от нас. Номерът се пази както е написан:
                    има доставчици с букви и тирета в него. */}
                <label className="field">
                  Фактура №
                  <input
                    placeholder="както е на фактурата"
                    value={supplierInvoiceNo}
                    onChange={(e) => setSupplierInvoiceNo(e.target.value)}
                  />
                </label>
                <label className="field">
                  Дата на фактурата
                  <DateField value={supplierInvoiceDate}
                    onChange={(v) => setSupplierInvoiceDate(v)}
                  />
                </label>
              </>
            ) : (
              <>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 7 }}>
                  <input
                    type="checkbox"
                    checked={issueInvoice}
                    onChange={(e) => setIssueInvoice(e.target.checked)}
                  />
                  издай фактура
                </label>
                {/*
                  * Номерът е ПРЕДПОЛОЖЕНИЕ, не запазено място.
                  *
                  * Взема се чак при записа, вътре в транзакцията и със
                  * заключен ред — така двама оператора не могат да получат
                  * един и същ номер. Ако се даваше отсега, отказана
                  * продажба щеше да го изгори и в поредността щеше да зейне
                  * дупка, а тя трябва да е без прекъсване.
                  */}
                {issueInvoice && (
                  <div className="field">
                    Номер на фактурата
                    <div className="field-static">
                      {nextInvoice
                        ? <span title="Номерът се дава при записа">
                            {nextInvoice} <span className="muted">(очакван)</span>
                          </span>
                        : <span className="muted">дава се при записа</span>}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/*
            * Избраното се разминава с картона — питаме дали да се запише.
            *
            * Стои под полето, а не в прозорец пред него: операторът въвежда
            * продажба и не бива да го спираме насред работата. И двата
            * отговора са едно натискане, а неотговарянето не пречи на нищо.
            */}
          {payAsk && (
            <div className="notice" style={{ marginTop: 12 }}>
              {payAsk.hadFixed
                ? <>В картона на <strong>{partner?.name}</strong> е записано
                    „{imeNa(payHint.fixed)}", а избрахте „{imeNa(payAsk.code)}".
                    Да запишем ли новото като уговорен начин?</>
                : <><strong>{partner?.name}</strong> няма уговорен начин на плащане.
                    Да запишем ли „{imeNa(payAsk.code)}" в картона?</>}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="button" className="primary"
                  onClick={() => zapishiVKarton(payAsk.code)}>
                  Запиши в картона
                </button>
                <button type="button" onClick={() => setPayAsk(null)}>
                  Само за тази продажба
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          Редове
          {!isPurchase && (
            <button
              type="button"
              className="link"
              style={{ float: 'right', fontWeight: 400 }}
              onClick={() => setShowCost((v) => !v)}
              title="Разходът обикновено се взима от партидата — тук се пише само когато се знае единствено от човека">
              {showCost ? 'Скрий разхода' : 'Ръчен разход'}
            </button>
          )}
        </div>
        <div className="card-body" style={{ paddingBottom: 8 }}>
          {chakashti.filter((u) => !dobaveniRefs.has(u.ref)).length > 0 && (
            <div className="warning" style={{ marginTop: 0, marginBottom: 10 }}>
              <strong>Чакат продажба за този клиент</strong>
              <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>изписани в Деливери, още не продадени</span>
              <table className="compact" style={{ marginTop: 6 }}>
                <tbody>
                  {chakashti.filter((u) => !dobaveniRefs.has(u.ref)).map((u) => (
                    <tr key={u.ref}>
                      <td>{u.vid === 'repair' ? 'Ремонт' : 'Зареждане'}</td>
                      <td><strong>{u.predmet}</strong>{u.serien ? <span className="muted"> · {u.serien}</span> : null}</td>
                      <td className="muted">{[u.litse, u.telefon].filter(Boolean).join(' · ')}</td>
                      <td className="muted">изписан {date(u.izpisan)}</td>
                      <td className="num" title={(u.chasti ?? []).map((c) => c.name).join(', ')}>
                        {u.cena != null ? money(u.cena + (u.chasti ?? []).reduce((a, c) => a + (c.sDds ?? 0), 0)) : '—'}
                        {(u.chasti ?? []).length > 0 && <span className="muted"> +{u.chasti.length} ч.</span>}
                      </td>
                      <td>
                        {u.productId
                          ? <button type="button" className="link" onClick={() => dobaviUsluga(u)}>Добави</button>
                          : <span className="muted" title="За този модел няма услуга в Тефтер">на ръка</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <label className="field" style={{ position: 'relative', maxWidth: 460 }}>
            Добавяне на стока
            {!isPurchase && (
              <span className="hint">
                цените се вземат по {priceLevel === 'wholesale' ? 'едро' : 'дребно'}
                {partner ? ` — както е зададено за ${partner.name}` : ''}
              </span>
            )}
            <input
              ref={productInput}
              className="tarsachka"
              type="search"
              autoComplete="off"
              placeholder="име, каталожен номер или бърз код"
              value={productQuery}
              onChange={(e) => { setProductQuery(e.target.value); setPokaziHits(true); }}
              // Текстът се маркира (08.10.2026): списъкът стои за следващия
              // цвят, а писането направо заменя старото търсене.
              onFocus={(e) => { setPokaziHits(true); e.target.select(); }}
              onClick={() => setPokaziHits(true)}
              onBlur={() => setTimeout(() => setPokaziHits(false), 150)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setPokaziHits(false); return; }
                klaviaturaStoka.onKeyDown(e);
              }}
            />
            {productHits.length > 0 && pokaziHits && (
              <Suggestions
                activeId={klaviaturaStoka.activeId}
                items={productHits}
                render={(p) => <ProductHit p={p} isPurchase={isPurchase} priceLevel={priceLevel}
                  zaeto={isPurchase ? 0 : zaetoNaEkrana(p.id)} />}
                onPick={razgledaj}
                groupBy={(p) => p.group_name}
                footer={productTotal > productHits.length
                  ? `показани ${productHits.length} от ${productTotal}`
                  : (productHits.length > 8 ? `намерени ${productHits.length}` : null)}
              />
            )}
          </label>

          {/* Бърза нова стока при доставка (06.10.2026) — без да се напуска
              документът. След „Създай" стоката се отваря в панела за
              доставка, както ако е избрана от търсенето. */}
          {isPurchase && !novaStoka && (
            <button type="button" style={{ marginLeft: 8 }} onClick={() => setNovaStoka(true)}>
              + нова стока
            </button>
          )}
          {isPurchase && novaStoka && (
            <div style={{ maxWidth: 760 }}>
              <NewProduct
                name={productQuery.trim()}
                onDone={(p) => { setNovaStoka(false); setProductQuery(''); razgledaj(p); }}
                onCancel={() => setNovaStoka(false)}
              />
            </div>
          )}

          {!isPurchase && (
            <label className="check" style={{ marginLeft: 12 }}>
              <input type="checkbox" checked={samoNalichni}
                onChange={(e) => setSamoNalichni(e.target.checked)} />
              само с наличност
            </label>
          )}
        </div>

        {gled && (gled.isPurchase ? (
          <PurchasePanel
            gled={gled}
            onChange={(patch) => setGled((g) => ({ ...g, ...patch }))}
            onAdd={dobaviDostavka}
            onClose={() => { setGled(null); productInput.current?.focus(); productInput.current?.select(); }}
          />
        ) : (
          <SalePanel
            gled={gled}
            lines={lines}
            onChange={(patch) => setGled((g) => ({ ...g, ...patch }))}
            onSell={prodaj}
            onClose={() => { setGled(null); productInput.current?.focus(); productInput.current?.select(); }}
          />
        ))}

        <table className="line-editor sbito">
          <thead>
            <tr>
              <th className="num" style={{ width: 34 }}>№</th>
              <th>Наименование</th>
              <th className="num" style={{ width: 110 }}>Количество</th>
              <th className="num" style={{ width: 130 }}>
                Ед. цена без ДДС
              </th>
              {!isPurchase && showCost && (
                <th className="num" style={{ width: 120 }} title="Оставено празно, се взима от партидата">
                  Ед. разход
                </th>
              )}
              {/* При продажба: вложеното в ремонт се маркира с едно
                  щракване. Нулева цена върши същото, но иска четири
                  действия — при доставка колоната няма смисъл. */}
              {!isPurchase && <th style={{ width: 80 }}>Подарък</th>}
              <th className="num" style={{ width: 70 }}>ДДС</th>
              <th className="num" style={{ width: 120 }}>Сума</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {podredeni(lines).map((l, index) => {
              // Сумата на реда е без ДДС, както е и единичната цена.
              const gross = l.isGift ? 0 : round2(Number(l.quantity || 0) * Number(l.price || 0));
              const short = !isPurchase && Number(l.quantity) > l.available;
              // Редът е в избор: или още няма стока, или се сменя.
              const choosing = !l.productId || l.picking;
              // Вързан със сигурност: по памет, бърз код, каталожен номер или на ръка.
              // „По прилика" не се брои — то още чака човешко око.
              const linked = !!l.productId && !l.picking && l.matchKind !== 'fuzzy';
              return (
                <tr key={l.key} className={linked ? 'linked' : undefined}>
                  <td className="num muted">{index + 1}</td>
                  <td>
                    {/* Докато се избира, редът от фактурата стои ОТГОРЕ:
                        падащият списък се отваря надолу и иначе покрива
                        точно това, с което трябва да се сравнява. */}
                    {choosing && l.supplierText && (
                      <div className="supplier-text" style={{ marginTop: 0, marginBottom: 4 }}>
                        по фактура: {l.supplierText}
                      </div>
                    )}
                    {l.productId && !l.picking ? (
                      <>
                        {/*
                          * Сбит ред (01.10.2026): името и бележката на един ред,
                          * подробностите за стоката — само при клик на името.
                          * Високите редове объркваха оператора.
                          */}
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <div className="our-product" style={{ cursor: 'pointer' }}
                            title={l.otvoren ? 'Скрий подробностите' : 'Покажи подробностите за стоката'}
                            onClick={(e) => {
                              if (e.target.closest('button')) return;
                              patchLine(l.key, { otvoren: !l.otvoren });
                            }}>
                            <span className="muted" style={{ fontSize: 10, marginRight: 5 }}>{l.otvoren ? '▾' : '▸'}</span>
                            {l.description}
                            {l.isStocked === false ? (
                              <span className="badge" style={{ marginLeft: 8 }}>услуга</span>
                            ) : short ? (
                              <span className="badge warn" style={{ marginLeft: 8 }}>
                                налично {quantity(l.available)}
                              </span>
                            ) : l.isGift && (
                              /* Тонер/чип, добавен автоматично към услугата — показва се и
                                 без да е недостатъчен, за да прецени операторът дали да
                                 поръча, преди складът наистина да свърши. */
                              <span className="badge muted" style={{ marginLeft: 8 }}>
                                налично {quantity(l.available)}
                              </span>
                            )}
                            {l.matchKind === 'fuzzy' && (
                              <>
                                <span className="badge warn" style={{ marginLeft: 8 }}
                                  title="Налучкано по модел — провери го">по прилика</span>
                                {/* Потвърждаването е един клик и се вижда:
                                    редът посинява и налучкването отпада. */}
                                <button className="link" style={{ marginLeft: 8 }}
                                  title="Да, това е вярната стока"
                                  onClick={() => confirmLine(l)}>
                                  потвърди
                                </button>
                              </>
                            )}
                            {/*
                              * Връзката се сменя само при доставка.
                              *
                              * Там редът още не е нищо продадено — само
                              * разчетен текст от чужда фактура, свързан с
                              * наша стока, и грешна връзка е поправка на
                              * данни. При продажба стоката вече е част от
                              * продажбата, закачена за нейния УНП (т. 9.1);
                              * тихо преиначаване на реда би заличило каква
                              * стока действително е влязла в екрана. Смяна
                              * там минава през анулиране на реда (✕) и нов
                              * ред за вярната стока — по т. 12.
                              */}
                            {isPurchase && (
                              <button className="link" style={{ marginLeft: 8 }}
                                title="Посочи друга наша стока"
                                onClick={() => patchLine(l.key, { picking: true })}>
                                смени
                              </button>
                            )}
                          </div>
                          {/* Бележката за реда излиза на фактурата — пише се и
                              поправя направо тук, докато продажбата не е записана. */}
                          <input
                            className="line-note"
                            value={l.note ?? ''}
                            placeholder="бележка за реда"
                            autoComplete="off"
                            maxLength={255}
                            onChange={(e) => patchLine(l.key, { note: e.target.value })}
                            style={{ flex: 1, minWidth: 160, maxWidth: 420, fontSize: 11.5, padding: '1px 6px' }}
                          />
                        </div>
                        {l.otvoren && (
                          <div style={{ marginTop: 2 }}>
                        {/* Кодът сам по себе си не казва каква е стоката —
                            затова се показва и описанието ѝ. */}
                        {l.productNote && (
                          <div className="muted" style={{ fontSize: 11 }}>{l.productNote}</div>
                        )}
                        {l.productGroup && (
                          <div className="muted" style={{ fontSize: 11 }}>{l.productGroup}</div>
                        )}
                        {/* Капацитетът стои до групата: по него се вижда
                            дали е същата касета, която пише на фактурата. */}
                        {l.productProperties && (
                          <div style={{ fontSize: 11 }}>{l.productProperties}</div>
                        )}
                        {/*
                          * Цените, партидата (при продажба) и историята на
                          * доставните цени (при доставка) се решават в
                          * панела, преди редът да влезе. Тук вече са
                          * решени — повтарянето им само задръства реда с
                          * числа, по които нищо не се прави.
                          *
                          * На тяхно място стои каквото описва стоката:
                          * каталожен номер, капацитет, бележката ѝ и
                          * бележката за самия ред, която излиза на
                          * фактурата.
                          */}
                        <LineInfo line={l} />
                          </div>
                        )}
                      </>
                    ) : (
                      // Разчетен ред без разпозната стока: посочва се тук
                      // и оттам нататък се помни за този доставчик.
                      <LinePicker line={l} partnerId={partner?.id} isPurchase={isPurchase}
                        onPick={(p) => linkImportLine(l, p)}
                        onCancel={l.productId ? () => patchLine(l.key, { picking: false }) : null} />
                    )}
                    {/* Текстът на доставчика е получер нарочно: на един
                        поглед да личи кое е негово и кое е нашата стока. */}
                    {!choosing && l.supplierText && (
                      <div className="supplier-text">по фактура: {l.supplierText}</div>
                    )}
                  </td>
                  <td className="num">
                    {/* Без стрелки: количеството рядко е кръгло и се пише
                        наведнъж, а стрелките само подлъгват при щракване. */}
                    <input
                      className="qty num"
                      inputMode="decimal"
                      value={l.quantity}
                      onChange={(e) => patchLine(l.key, {
                        // При доставка — цяло число: дробна бройка няма
                        // от 2019 г. насам. При продажба тонерът върви
                        // на килограм и дробното е ежедневие.
                        quantity: isPurchase
                          ? String(e.target.value).replace(/[^\d]/g, '')
                          : e.target.value,
                      })}
                    />
                  </td>
                  <td className="num">
                    <NumBox
                      className="price num"
                      value={l.isGift ? '' : l.price}
                      disabled={l.isGift}
                      onChange={(v) => patchLine(l.key, { price: v })}
                    />
                  </td>
                  {!isPurchase && showCost && (
                    <td className="num">
                      {/* Празно е обичайното: себестойността идва от
                          партидата. Пише се на ръка само когато стоката
                          не е минала през склада — фиктивни фактури,
                          услуги с вложен разход. */}
                      <NumBox
                        className="price num"
                        placeholder="сама"
                        title="Празно — взима се от партидата, от която излиза стоката"
                        value={l.unitCost ?? ''}
                        onChange={(v) => patchLine(l.key, { unitCost: v })}
                      />
                    </td>
                  )}
                  {!isPurchase && (
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!l.isGift}
                        title="Вложено в ремонт — без пари, но се изписва от склада"
                        onChange={(e) => patchLine(l.key, { isGift: e.target.checked })}
                      />
                    </td>
                  )}
                  <td className="num muted">{l.isGift ? '' : `${l.vatRate}%`}</td>
                  <td className="num">
                    {l.isGift ? <span className="badge muted">подарък</span> : money(gross)}
                  </td>
                  <td>
                    <button
                      className="link"
                      title={isPurchase ? 'Премахни реда' : 'Анулирай реда'}
                      onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                    >
                      {isPurchase ? 'Премахни' : 'Анулирай'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {lines.length === 0 && (
              <tr><td colSpan={7 + (isPurchase ? 0 : 1) + (!isPurchase && showCost ? 1 : 0)} className="empty">Добавете стока, за да започнете</td></tr>
            )}
          </tbody>
        </table>

        <div className="card-body">
          <div className="totals">
            <div><span className="muted">Данъчна основа</span><span>{money(totals.net)}</span></div>
            <div><span className="muted">ДДС</span><span>{money(totals.vat)}</span></div>
            <div className="grand"><span>Общо</span><span>{money(totals.gross)}</span></div>
          </div>

          {/* Записът стои под сумата, а не в отделна карта долу вляво:
              работата по този екран свършва в последния ред вдясно и
              копчето трябва да е на една ръка разстояние оттам. */}
          <div style={{
            display: 'flex', gap: 12, marginTop: 14,
            justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap',
          }}>
            {myposPayment?.status === 'InProgress' ? (
              <div className="mypos-banner">
                Чака плащане на терминала ({money(totals.gross)})…
                <button type="button" className="link"
                  onClick={() => { myposCancelRef.current = true; }}>
                  Откажи
                </button>
                {myposBavi && (
                  <div style={{ marginTop: 6, fontSize: 12, fontWeight: 400 }}>
                    Заявката е изпратена, но терминалът не я поема. Отворете
                    POSLink Manager на терминала и изчакайте; ако не тръгне —
                    рестартирайте терминала, после „Откажи“ и пак „Запис“.
                  </div>
                )}
              </div>
            ) : myposPayment?.status === 'Success' ? (
              <div className="mypos-banner success">
                ✓ Плащането мина успешно ({money(totals.gross)})
              </div>
            ) : (
              <span className="hint" style={{ textAlign: 'right' }}>
                {unlinked > 0
                  ? `Първо посочи стока на ${unlinked} ${unlinked === 1 ? 'ред' : 'реда'}.`
                  : nadNalichnost
                    ? 'Има ред с количество над наличното — свали го или смени стоката.'
                    : (!isPurchase && paymentMethod === 'card')
                      ? 'Ще прати заявка за плащане към терминала.'
                      : editDoc
                        ? 'Поправката се записва в документа; махнатите редове остават анулирани.'
                      : (!isPurchase && ['bank', 'postal'].includes(paymentMethod))
                        ? 'Плащане без касова бележка — записаният документ ще може да се поправя.'
                        : 'Записаният документ не се редактира — само се сторнира.'}
              </span>
            )}
            {/* Отказ на цялата доставка (13.10.2026). Тя няма чернова на
                сървъра — всичко е само на екрана, затова отказът е чисто
                зареждане наново; нищо в базата не се пипа. */}
            {isPurchase && !editDoc && (lines.length > 0 || partner || supplierInvoiceNo.trim()) && (
              <button type="button" disabled={busy}
                onClick={() => {
                  if (window.confirm('Да откажа ли цялата доставка? Въведеното ще се изчисти и няма да се запише.')) {
                    window.location.assign('/documents/new/purchase');
                  }
                }}>
                Откажи документа
              </button>
            )}
            <button className="primary" onClick={() => save('confirmed')}
              disabled={!ready || busy || (!editDoc && credit?.blocked)}
              title={credit?.blocked ? 'Продажбите към този клиент са спрени' : undefined}>
              Запис
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {/* Две полета, не едно: има доставчици, които по една и съща
              фактура правят отстъпка за целия документ И карат
              транспорта за наша сметка. Събрани в едно число, двете се
              изяждат взаимно. И двете се пишат положителни — знакът го
              носи името на полето, не стойността. */}
          {isPurchase && (
            <div style={{ maxWidth: 420 }}>
              {/* Две полета с празно между тях (27.10.2026) — в .toolbar с
                  maxWidth етикетът се свиваше, а полето оставаше широко и
                  второто застъпваше първото. */}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 4 }}>
                <label className="field" style={{ width: 180 }}>
                  Транспорт за наша сметка
                  <NumBox className="num" placeholder="0.00" style={{ width: '100%', boxSizing: 'border-box' }}
                    value={transportCost}
                    onChange={setTransportCost} />
                </label>
                <label className="field" style={{ width: 180 }}>
                  Отстъпка по фактурата
                  <NumBox className="num" placeholder="0.00" style={{ width: '100%', boxSizing: 'border-box' }}
                    value={invoiceDiscount}
                    onChange={setInvoiceDiscount} />
                </label>
              </div>
              <span className="hint">
                Отстъпката е ред от фактурата и сваля сумата на доставката.
                Транспортът „за наша сметка" не я пипа — той само вдига
                себестойността. И двете се разнасят по редовете съразмерно на
                стойността им: транспортът я вдига, отстъпката я сваля.
                {extraNet !== 0 && (
                  <> Върху себестойността ляга{' '}
                    <b>{extraNet > 0 ? '+' : '−'}{Math.abs(extraNet).toFixed(2)} €</b>.</>
                )}
              </span>
            </div>
          )}
          <label className="field">
            Забележка
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>

        </div>
      </div>
      </>
      )}

      {addingPartner && (
        <QuickPartner
          type={isPurchase ? 'supplier' : 'client'}
          onClose={() => setAddingPartner(false)}
          onCreated={choosePartner}
        />
      )}

      {editingPartner && partner && (
        <QuickPartner
          type={isPurchase ? 'supplier' : 'client'}
          editId={partner.id}
          onClose={() => setEditingPartner(false)}
          onCreated={(p) => {
            setPartner((cur) => ({ ...cur, ...p, id: cur.id }));
            setPartnerQuery(p.name ?? '');
          }}
        />
      )}

      {voiding && (
        <VoidReasonModal
          unp={draft?.unp}
          onClose={() => setVoiding(false)}
          onConfirm={anuliraj}
        />
      )}
    </>
  );
}

/**
 * Старите продажби/доставки, показани под същите табове.
 *
 * Копие на филтрите от Documents.jsx, но с локално състояние вместо
 * адреса — тук не е самостоятелна страница, а превключваем изглед под
 * табовете „Продажба/Доставка/Стари…“, затова няма нужда „Назад“ на
 * браузъра да го помни през URL.
 */
function OldDocuments({ type, initialStatus = '' }) {
  // Без страниране — списъкът се скролва вътре в себе си (виж
  // .table-wrap по-долу, сведен до ~11 реда), затова се тегли наведнъж
  // солиден брой редове вместо страница по 50.
  const LIMIT = 500;
  /*
   * Филтрите и маркираният ред живеят в адреса, не в паметта на екрана.
   *
   * Отваря се фактура от списъка, не е търсената — „Назад" в браузъра
   * трябва да върне същото търсене, не празен списък. Пише се с
   * `replace`, за да не трупа всяка буква по стъпка в историята.
   */
  const [params, setParams] = useSearchParams();
  const filters = {
    status: params.get('f_status') ?? initialStatus,
    from: params.get('f_from') ?? '',
    to: params.get('f_to') ?? '',
    q: params.get('f_q') ?? '',
    partnerQ: params.get('f_partner') ?? '',
    unpaidOnly: params.get('f_unpaid') === '1',
    groupInvoices: params.get('f_group') === '1',
  };
  const selectedId = params.get('sel') ? Number(params.get('sel')) : null;
  const navigateOld = useNavigate();
  const spisakRef = useRef(null);
  // Отвореният документ знае откъде е дошъл — „← Към списъка“ връща тук.
  const loc = useLocation();
  const otSpisak = { otSpisak: loc.pathname + loc.search };
  const [data, setData] = useState({ items: [], total: 0, sumBase: '0.00' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statementOpen, setStatementOpen] = useState(false);
  const [invoiceMailOpen, setInvoiceMailOpen] = useState(false);

  // Екранът е на два етажа, както в Генсофт: горе списъкът, долу редовете
  // на маркираната продажба/доставка — без да се напуска страницата.
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedError, setSelectedError] = useState(null);

  // Справка по имейл има смисъл само към ЕДИН контрагент — ако филтърът
  // връща няколко (или продажби на каса без контрагент), копчето е
  // изключено, вместо да гадае към кого да прати.
  const partnerIds = [...new Set(data.items.map((d) => d.partner_id).filter(Boolean))];
  const canStatement = partnerIds.length === 1 && data.items.length > 0;
  // При доставка фактурата е на доставчика и има своя дата, различна от
  // деня на въвеждане; при продажба двете съвпадат.
  const sDataFaktura = type === 'purchase';

  /*
   * Смяната на таба (продажби ↔ доставки ↔ чернови) тръгва от чисто
   * търсене. Само при истинска смяна — не при връщане с „Назад", когато
   * екранът се сглобява наново със същия таб и филтрите в адреса.
   */
  const predishen = useRef({ type, initialStatus });
  useEffect(() => {
    const p = predishen.current;
    predishen.current = { type, initialStatus };
    if (p.type === type && p.initialStatus === initialStatus) return;
    setParams((cur) => {
      const next = new URLSearchParams(cur);
      for (const k of ['f_status', 'f_from', 'f_to', 'f_q', 'f_partner', 'f_unpaid', 'f_group', 'sel']) next.delete(k);
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, initialStatus]);

  const KLYUCH = {
    status: 'f_status', from: 'f_from', to: 'f_to', q: 'f_q',
    partnerQ: 'f_partner', unpaidOnly: 'f_unpaid', groupInvoices: 'f_group',
  };
  function setSelectedId(id) {
    setParams((cur) => {
      const next = new URLSearchParams(cur);
      if (id) next.set('sel', String(id)); else next.delete('sel');
      return next;
    }, { replace: true });
  }

  /*
   * Списъкът и със стрелките (30.10.2026, Светлана) — ↑/↓ местят маркера и
   * долу веднага излизат редовете; PgUp/PgDn по 10, Home/End, Enter отваря
   * документа. ↓ от полетата „Контрагент“/„Търсене“ слиза в списъка.
   */
  const linkNa = (d) => (d.documentCount > 1 ? `/print/invoice/${d.invoice_no}` : `/documents/${d.id}`);
  function vlezVSpisaka() {
    spisakRef.current?.focus();
    if (!data.items.some((d) => d.id === selectedId) && data.items[0]) setSelectedId(data.items[0].id);
  }
  function onSpisakKey(e) {
    const items = data.items;
    if (!items.length) return;
    const tek = items.findIndex((d) => d.id === selectedId);
    const kam = (i) => setSelectedId(items[Math.max(0, Math.min(items.length - 1, i))].id);
    const k = e.key;
    if (k === 'ArrowDown') kam(tek < 0 ? 0 : tek + 1);
    else if (k === 'ArrowUp') kam(tek < 0 ? 0 : tek - 1);
    else if (k === 'PageDown') kam(tek + 10);
    else if (k === 'PageUp') kam(tek - 10);
    else if (k === 'Home') kam(0);
    else if (k === 'End') kam(items.length - 1);
    else if (k === 'Enter' && tek >= 0) navigateOld(linkNa(items[tek]), { state: otSpisak });
    else return;
    e.preventDefault();
  }
  useEffect(() => {
    spisakRef.current?.querySelector('tr.open')?.scrollIntoView({ block: 'nearest' });
  }, [selectedId, data.items]);

  useEffect(() => {
    if (!selectedId) { setSelectedDoc(null); setSelectedError(null); return undefined; }
    let cancelled = false;
    setSelectedLoading(true);
    setSelectedError(null);
    api.get(`/api/documents/${selectedId}`)
      .then((d) => { if (!cancelled) setSelectedDoc(d); })
      .catch((err) => { if (!cancelled) setSelectedError(err.message); })
      .finally(() => { if (!cancelled) setSelectedLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      api.get(withQuery('/api/documents', { type, ...filters, limit: LIMIT, offset: 0 }))
        .then(setData)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }, (filters.q || filters.partnerQ) ? 250 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, JSON.stringify(filters)]);

  function change(patch) {
    setParams((cur) => {
      const next = new URLSearchParams(cur);
      for (const [k, v] of Object.entries(patch)) {
        const key = KLYUCH[k];
        if (!key) continue;
        const val = typeof v === 'boolean' ? (v ? '1' : '') : (v ?? '');
        // Състоянието по подразбиране (напр. „чернова") се пази и празно.
        if (val === '' && !(k === 'status' && initialStatus)) next.delete(key);
        else next.set(key, val);
      }
      return next;
    }, { replace: true });
  }

  return (
    <>
      <div className="toolbar" style={{ marginTop: 0 }}>
        <span className="muted" style={{ paddingBottom: 7 }}>общо {money(data.sumBase)}</span>
        <div className="spacer" />
        <a
          className="button"
          href={withQuery('/api/documents.xlsx', { type, ...filters })}
          download
        >
          Excel
        </a>
        <button
          disabled={!canStatement}
          title={canStatement ? undefined
            : 'Стеснете филтъра до един контрагент, за да прати справка'}
          onClick={() => setStatementOpen(true)}
        >
          Справка по имейл
        </button>
        {/* Няколко фактури/КИ като отделни PDF в едно писмо (12.10.2026). */}
        {type === 'sale' && (
          <button
            disabled={!canStatement}
            title={canStatement ? undefined
              : 'Стеснете филтъра до един контрагент, за да прати фактурите му'}
            onClick={() => setInvoiceMailOpen(true)}
          >
            Фактури по имейл
          </button>
        )}
      </div>

      {invoiceMailOpen && (
        <InvoiceMailModal
          partnerId={partnerIds[0]}
          from={filters.from}
          to={filters.to}
          onClose={() => setInvoiceMailOpen(false)}
        />
      )}

      {statementOpen && (
        <StatementModal
          type={type}
          filters={filters}
          onClose={() => setStatementOpen(false)}
        />
      )}

      {error && <div className="error">{error}</div>}

      <div className="toolbar">
        <label className="field">
          Контрагент
          <input
            type="search"
            autoComplete="off"
            placeholder="име, ЕИК, ДДС номер или телефон"
            value={filters.partnerQ}
            onChange={(e) => change({ partnerQ: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'ArrowDown') { e.preventDefault(); vlezVSpisaka(); } }}
          />
        </label>
        <label className="field">
          Търсене
          <input
            type="search"
            autoComplete="off"
            placeholder="номер, фактура или УНП"
            value={filters.q}
            onChange={(e) => change({ q: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'ArrowDown') { e.preventDefault(); vlezVSpisaka(); } }}
          />
        </label>
        <label className="field">
          Състояние
          <select value={filters.status} onChange={(e) => change({ status: e.target.value })}>
            <option value="">всички</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="field">
          От дата
          <DateField value={filters.from} onChange={(v) => change({ from: v })} />
        </label>
        <label className="field">
          До дата
          <DateField value={filters.to} onChange={(v) => change({ to: v })} />
        </label>
        <div className="field">
          <span aria-hidden="true" style={{ visibility: 'hidden' }}>·</span>
          <QuickPeriod onPick={({ from, to }) => change({ from, to })} />
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 7 }}>
          <input
            type="checkbox"
            checked={filters.unpaidOnly}
            onChange={(e) => change({ unpaidOnly: e.target.checked })}
          />
          само неплатени
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 7 }}>
          <input
            type="checkbox"
            checked={filters.groupInvoices}
            onChange={(e) => change({ groupInvoices: e.target.checked })}
          />
          групирай по фактура
        </label>
      </div>

      <div className="card">
        {/* ~11 реда видими наведнъж (ред ≈ 19px при сбитата таблица), останалото със скрол вътре. */}
        <div className="table-wrap" style={{ maxHeight: 230, outline: 'none' }}
          ref={spisakRef} tabIndex={0} onKeyDown={onSpisakKey}
          title="↑/↓ — маркер, Enter — отвори">
          <table className="compact">
            <thead>
              <tr>
                <th>Контрагент</th>
                <th>Дата</th>
                <th>Фактура</th>
                {/* Датата на самата фактура на доставчика — „Дата" отляво е
                    денят, в който е въведена (12.10.2026). */}
                {sDataFaktura && <th>Дата ф-ра</th>}
                <th>Документ</th>
                <th>Склад</th>
                <th>Плащане</th>
                <th className="num">Сума</th>
                <th className="num">Платено</th>
                <th>Състояние</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((d) => {
                const grouped = d.documentCount > 1;
                const docLink = linkNa(d);
                return (
                  <tr
                    key={grouped ? `inv${d.invoice_no}` : d.id}
                    className={`clickable ${d.id === selectedId ? 'open' : ''}`}
                    onClick={() => { setSelectedId(d.id); spisakRef.current?.focus(); }}
                  >
                    <td>
                      <strong>
                        {d.partner_name
                          ? <Link to={docLink} state={otSpisak} onClick={(e) => e.stopPropagation()}>{d.partner_name}</Link>
                          : <span className="muted">на каса</span>}
                      </strong>
                    </td>
                    <td>{date(d.doc_date)}</td>
                    <td className="muted">{d.invoice_no ?? '—'}</td>
                    {sDataFaktura && <td className="muted">{d.invoice_date ? date(d.invoice_date) : '—'}</td>}
                    <td>
                      {grouped
                        ? <Link to={docLink} state={otSpisak} onClick={(e) => e.stopPropagation()}>
                            {d.documentCount} документа
                          </Link>
                        : <Link to={docLink} state={otSpisak} onClick={(e) => e.stopPropagation()}>{d.doc_number}</Link>}
                    </td>
                    <td className="muted">{d.warehouse_name ?? '—'}</td>
                    {/* Начинът на плащане (06.10.2026) — в брой, разсрочено,
                        банка, ПОС… */}
                    <td className="muted">{PAYMENT_LABEL[d.payment_method] ?? d.payment_method ?? '—'}</td>
                    <td className="num">{money(d.total, d.currency)}</td>
                    <td className="num">
                      {Number(d.paid_amount) >= Number(d.total)
                        ? <span className="badge ok">платено</span>
                        : money(d.paid_amount, d.currency)}
                    </td>
                    <td><span className={`badge ${STATUS_CLASS[d.status]}`}>{STATUS_LABEL[d.status]}</span></td>
                  </tr>
                );
              })}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={sDataFaktura ? 10 : 9} className="empty">Няма намерени документи</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {data.total > data.items.length && (
          <div className="muted" style={{ padding: '6px 8px', fontSize: 12 }}>
            показани първите {data.items.length} от {data.total} — стеснете търсенето за повече
          </div>
        )}
      </div>

      <SelectedDocumentLines
        doc={selectedDoc}
        loading={selectedLoading}
        error={selectedError}
      />
    </>
  );
}

/**
 * Долният етаж на екрана — редовете на маркираната по-горе продажба/
 * доставка, точно както в Генсофт: горе списъкът, долу разбивката, без
 * да се сменя страницата.
 */
/*
 * Издадената фактура на маркирания документ — в прегледа за печат, откъдето
 * са Печат, PDF и Мейл (03.11.2026, Светлана). Обединената фактура (няколко
 * продажби с един номер) се отваря цялата, иначе — печатът на документа.
 * Прозорецът се отваря веднага, преди заявката — иначе браузърът го спира.
 */
async function otvoriFaktura(doc) {
  const w = window.open('', '_blank');
  let url = `/print/document/${doc.id}?form=invoice`;
  try {
    const inv = await api.get(`/api/invoices/${encodeURIComponent(doc.invoice_no)}`);
    if ((inv.documents ?? []).length > 1) url = `/print/invoice/${encodeURIComponent(doc.invoice_no)}`;
  } catch { /* остава печатът на документа */ }
  if (w) w.location.href = url; else window.location.href = url;
}

export function SelectedDocumentLines({ doc, loading, error }) {
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head">
        Редове{doc ? ` — ${DOC_TYPE_LABEL[doc.doc_type] ?? 'документ'} № ${doc.doc_number}` : ''}
        {doc?.invoice_no && (
          <>
            <span style={{ flex: 1 }} />
            <button type="button" className="primary" onClick={() => otvoriFaktura(doc)}
              title="Печат, PDF или мейл на фактурата">
              Фактура № {doc.invoice_no}
            </button>
          </>
        )}
      </div>
      {!doc && !loading && (
        <div className="empty">Маркирай ред от списъка отгоре, за да видиш какво съдържа</div>
      )}
      {loading && <div className="empty">Зареждане…</div>}
      {error && <div className="error">{error}</div>}
      {doc && !loading && (
        <div className="table-wrap" style={{ maxHeight: '32vh' }}>
          <table className="compact">
            <thead>
              <tr>
                <th style={{ width: 40 }}>№</th>
                <th>Наименование</th>
                <th>Бележка на стоката</th>
                <th>Капацитет</th>
                <th>Бележка на реда</th>
                <th className="num">Количество</th>
                <th className="num">Ед. цена без ДДС</th>
                <th className="num">ДДС</th>
                <th className="num">Сума без ДДС</th>
              </tr>
            </thead>
            <tbody>
              {doc.items.map((it) => {
                const anuliran = Number(it.is_cancelled) === 1;
                const style = anuliran ? { textDecoration: 'line-through' } : undefined;
                return (
                  <tr key={it.id} className={anuliran ? 'muted' : undefined}>
                    <td className="muted">{it.line_no}</td>
                    <td style={style}>
                      {it.product_id
                        ? <Link to={`/products/${it.product_id}`}>{it.description}</Link>
                        : it.description}
                      {Number(it.is_gift) === 1 && (
                        <span className="badge muted" style={{ marginLeft: 6 }}>подарък</span>
                      )}
                      {anuliran && (
                        <span className="badge muted" style={{ marginLeft: 6 }}>анулиран</span>
                      )}
                    </td>
                    <td className="muted" style={style}>{it.product_note ?? '—'}</td>
                    <td className="muted" style={style}>{it.product_capacity ?? '—'}</td>
                    <td className="muted" style={style}>{it.note ?? '—'}</td>
                    <td className="num" style={style}>{quantity(it.quantity)} {it.unit}</td>
                    <td className="num" style={style}>
                      {Number(it.is_gift) === 1 ? '—' : unitPrice(it, it.currency)}
                    </td>
                    <td className="num muted" style={style}>
                      {Number(it.is_gift) === 1 ? '' : `${Number(it.vat_rate)}%`}
                    </td>
                    <td className="num" style={style}>{money(it.line_subtotal, it.currency)}</td>
                  </tr>
                );
              })}
              {doc.items.length === 0 && (
                <tr><td colSpan={9} className="empty">Документът няма редове</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Справка за издадени документи, изпратена директно на клиента.
 *
 * Две стъпки нарочно, както при напомнянето за дълг в „Длъжници": първо се
 * вижда самото писмо — до кого, за какъв период, колко документа — чак
 * тогава тръгва. Филтърът е същият, с който борави екранът зад модала,
 * затова справката показва точно каквото операторът вижда.
 */
/**
 * „Фактури по имейл" (12.10.2026) — няколко фактури и кредитни известия
 * на един клиент в едно писмо, всяка като отделен PDF. Списъкът е за
 * периода от филтъра на „Стари продажби"; нищо не е отметнато
 * предварително — операторът избира сам или с „Маркирай всички".
 */
function InvoiceMailModal({ partnerId, from, to, onClose }) {
  const [data, setData] = useState(null);
  const [izbrani, setIzbrani] = useState(new Set());
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [email, setEmail] = useState('');

  function zaredi() {
    setBusy(true);
    setError(null);
    return api.get(withQuery('/api/documents/invoice-mail/preview', { partnerId, from, to }))
      .then((r) => {
        setData(r);
        // Нищо не е отметнато предварително (12.10.2026) — операторът избира.
        setIzbrani(new Set());
      })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  }

  useEffect(() => {
    zaredi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function obarni(key) {
    setIzbrani((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function zapishiEmail() {
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) { setError('Невалиден имейл адрес'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/partners/${partnerId}`, { email: value });
      const kept = izbrani;
      await zaredi();
      setIzbrani(kept);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const keys = data.items.filter((g) => izbrani.has(g.key)).map((g) => g.key);
      const res = await api.post('/api/documents/invoice-mail/send', { partnerId, keys });
      setDone(res.redirected
        ? `Пробен режим — писмото отиде при нас (${res.to}), не при клиента.`
        : `Изпратени ${res.count} документа до ${res.to}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const items = data?.items ?? [];
  const broi = items.filter((g) => izbrani.has(g.key)).length;
  const suma = items.filter((g) => izbrani.has(g.key))
    .reduce((s, g) => s + (g.kind === 'credit' ? -1 : 1) * Number(g.totalBase), 0);
  const mnogo = data && broi > data.max;
  const mozhe = data && data.to && data.mailReady && broi > 0 && !mnogo;

  return (
    <div className="modal-backdrop">
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 720 }}>
        <div className="card-head">
          Фактури по имейл{data ? ` до ${data.partner.name}` : ''}
        </div>
        <div className="card-body">
          {busy && !data && !error && <div className="empty">Зареждане…</div>}
          {error && <div className="error">{error}</div>}
          {done && <div className="notice">{done}</div>}

          {data && !done && (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <strong>До:</strong>
                {data.to || (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <input
                      type="email"
                      autoComplete="off"
                      placeholder="имейл на клиента"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <button type="button" disabled={busy} onClick={zapishiEmail}>запиши</button>
                  </span>
                )}
                <span className="muted">
                  {from || to
                    ? `период ${from ? date(from) : '…'} – ${to ? date(to) : '…'}`
                    : 'за целия период'}
                </span>
              </div>

              {items.length === 0 ? (
                <div className="empty">Няма фактури и кредитни известия за този период</div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 12, margin: '8px 0 4px' }}>
                    <button type="button"
                      onClick={() => setIzbrani(new Set(items.map((g) => g.key)))}>
                      Маркирай всички
                    </button>
                    <button type="button" disabled={broi === 0} onClick={() => setIzbrani(new Set())}>
                      Размаркирай
                    </button>
                    <span className="spacer" />
                    <span className="muted">избрани {broi} · {money(suma)}</span>
                  </div>
                  <div className="table-wrap" style={{ maxHeight: '45vh' }}>
                    <table className="compact">
                      <thead>
                        <tr>
                          <th style={{ width: 28 }} />
                          <th>Вид</th>
                          <th>Номер</th>
                          <th>Дата</th>
                          <th>Продажба</th>
                          <th className="num">Сума</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((g) => (
                          <tr key={g.key} className="clickable" onClick={() => obarni(g.key)}>
                            <td>
                              <input
                                type="checkbox"
                                checked={izbrani.has(g.key)}
                                onChange={() => obarni(g.key)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td>
                              {g.kind === 'credit' ? 'Кредитно известие' : 'Фактура'}
                              {g.status !== 'confirmed' && (
                                <span className={`badge ${STATUS_CLASS[g.status] ?? ''}`} style={{ marginLeft: 6 }}>
                                  {STATUS_LABEL[g.status] ?? g.status}
                                </span>
                              )}
                            </td>
                            <td>{g.invoiceNo}</td>
                            <td>{date(g.date)}</td>
                            <td className="muted">
                              {g.docNumbers.length > 1 ? `${g.docNumbers.length} продажби` : g.docNumbers[0]}
                            </td>
                            <td className="num">
                              {g.kind === 'credit' ? '−' : ''}{money(g.total, g.currency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {mnogo && (
                <div className="error" style={{ marginTop: 8 }}>
                  Най-много {data.max} документа в едно писмо — махнете отметките на част
                  от тях или стеснете периода.
                </div>
              )}
              {!data.mailReady && <div className="muted" style={{ marginTop: 8 }}>пощата не е настроена</div>}
              {busy && data && (
                <div className="muted" style={{ marginTop: 8 }}>
                  Подготвям {broi} PDF и изпращам — може да отнеме до минута…
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-foot">
          {done || !data ? (
            <button onClick={onClose}>Затвори</button>
          ) : (
            <>
              <button onClick={onClose} disabled={busy}>Откажи</button>
              <button className="primary" disabled={busy || !mozhe} onClick={send}>
                Изпрати {broi > 0 ? `(${broi})` : ''}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatementModal({ type, filters, onClose }) {
  const [letter, setLetter] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(withQuery('/api/documents/statement/preview', { type, ...filters }))
      .then((r) => { if (!cancelled) setLetter(r); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(withQuery('/api/documents/statement/send', { type, ...filters }), {});
      setDone(res.redirected
        ? `Пробен режим — писмото отиде при нас (${res.to}), не при клиента.`
        : `Изпратено до ${res.to}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 640 }}>
        <div className="card-head">
          Справка по имейл{letter ? ` до ${letter.partner.name}` : ''}
        </div>
        <div className="card-body">
          {busy && !letter && !error && <div className="empty">Зареждане…</div>}
          {error && <div className="error">{error}</div>}
          {done && <div className="notice">{done}</div>}

          {letter && !done && (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <strong>До:</strong> {letter.to ?? '— няма записан имейл —'}
                <span className="muted">{letter.rows.length} документа · {money(letter.total)}</span>
              </div>

              <div style={{ marginTop: 6 }}><strong>Тема:</strong> {letter.subject}</div>

              <pre style={{
                whiteSpace: 'pre-wrap', margin: '10px 0', padding: 10,
                background: 'var(--surface-2)', border: '1px solid var(--border)',
                borderRadius: 6, fontSize: 12, maxHeight: '40vh', overflowY: 'auto',
              }}
              >
                {letter.text}
              </pre>

              {!letter.canSend && (
                <div className="muted" style={{ marginTop: 8 }}>
                  {letter.to ? 'пощата не е настроена' : 'няма записан имейл на контрагента'}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-foot">
          {done || !letter ? (
            <button onClick={onClose}>Затвори</button>
          ) : (
            <>
              <button onClick={onClose} disabled={busy}>Откажи</button>
              <button className="primary" disabled={busy || !letter.canSend} onClick={send}>
                Изпрати
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Ред в подсказките при търсене на стока.
 *
 * Наименованието тук е каталожен код („101CANCRG716B“) и само по себе си
 * не казва какво се продава. Затова се показват и бележката, която е
 * човешкото описание, характеристиките и цената, с която стоката ще
 * влезе в документа — за да не се налага отваряне на картата.
 */
/** „последна доставка: 3,66 € от …, дата" — в търсенето при доставка (13.10.2026). */
export function PoslednaDostavka({ p }) {
  return (
    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
      {p.last_purchase ? (
        <>
          последна доставка: <strong>{money(p.last_purchase.price)}</strong>
          {' от '}{p.last_purchase.partnerName ?? '—'}, {date(p.last_purchase.date)}
          {p.last_purchase_same && (
            <> · от този доставчик: {money(p.last_purchase_same.price)}, {date(p.last_purchase_same.date)}</>
          )}
        </>
      ) : 'не е доставяна досега'}
    </div>
  );
}

function ProductHit({ p, isPurchase, priceLevel, zaeto = 0 }) {
  // При доставка се показва последната доставна цена — тя е и цената,
  // която застава в полето. Няма ли доставки, остава тази от картона.
  const price = isPurchase
    ? (p.last_purchase_price ?? p.purchase_price)
    : (priceLevel === 'wholesale'
        ? (p.wholesale_price ?? p.retail_price)
        : (p.retail_price ?? p.wholesale_price));

  // Наличността минус вече добавеното на екрана, още незаписано (02.10.2026).
  const qty = Number(p.quantity) - zaeto;
  const second = [p.note, p.properties_text].filter(Boolean).join(' · ');

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <strong>{p.name}</strong>
        {p.code && <span className="muted">{p.code}</span>}
        <span style={{ flex: 1 }} />
        <span
          className={qty > 0 ? '' : 'muted'}
          style={qty > 0 ? { fontWeight: 700, color: 'var(--accent)' } : undefined}
        >
          {quantity(qty)} {p.unit}
          {zaeto > 0 && (
            <span className="badge ok" style={{ marginLeft: 6, fontWeight: 600, fontSize: 11 }}>
              ✓ в продажбата: {quantity(zaeto)}
            </span>
          )}
        </span>
      </div>
      {second && (
        <div className="muted" style={{
          fontSize: 12, marginTop: 2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {second}
        </div>
      )}
      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
        {price != null && <strong>{money(price, p.currency)}</strong>}
        {!!Number(p.is_batch_tracked) && ' · на партиди'}
      </div>
      {/* Предишната доставка — по колко и от кого (13.10.2026). */}
      {isPurchase && <PoslednaDostavka p={p} />}
    </>
  );
}

/**
 * Кога и по колко е доставяна стоката за последно.
 *
 * Два реда: от този доставчик и от друг. Цената се сравнява с въведената
 * сега — поскъпването се вижда веднага, а не в справка след месец.
 * Цените са без ДДС, каквато е и въвежданата по доставка.
 *
 * Докато върви пробният режим се показва и трети ред — последната пробна
 * доставка. Тя стои отделно и с личба, за да не се бърка с истинската
 * цена, но да си личи по колко е карана стоката при пробите.
 */
/**
 * Изборът на партида при продажба.
 *
 * Стоката излиза от най-старата партида, освен ако не се посочи друга.
 * Обикновено това е правилното и не иска внимание — затова редът мълчи,
 * докато партидите са една или доставните им цени съвпадат.
 *
 * Разминат ли се обаче, изборът решава дали сделката е на печалба. При
 * продажба на едро с тънка надценка една и съща касета може да лежи в
 * две партиди с чувствително различна цена и продажбата от скъпата излиза
 * на загуба, без нищо да го покаже. Тогава редът показва разликата и
 * оставя човека да избере.
 */
/**
 * Наличността на тонера/чипа до избора (07.10.2026). Червено, ако няма
 * колкото иска зареждането — тогава изписването би спряло на „Запис“.
 */
function Nalichno({ c }) {
  if (c.onHand == null) return null;
  const nuzhno = c.quantityUnits != null ? Number(c.quantityUnits) : 1;
  const malko = Number(c.onHand) < nuzhno - 1e-9;
  return (
    <span className={malko ? 'badge danger' : 'badge'} style={{ fontSize: 11 }}>
      {Number(c.onHand) > 0 ? `налични ${quantity(c.onHand)} ${c.unit ?? ''}` : 'няма'}
    </span>
  );
}

function BatchPick({ line, onPick }) {
  const batches = line.batches;
  if (!batches || batches.length === 0) return null;

  const ceni = [...new Set(batches.map((b) => (b.price == null ? 'нула' : b.price.toFixed(4))))];
  const razlichni = ceni.length > 1;

  // Една партида или еднакви цени — няма какво да се избира.
  if (batches.length === 1 || !razlichni) return null;

  const izbrana = batches.find((b) => String(b.id) === String(line.batchId));
  const nayStara = batches[0];
  const cena = izbrana?.price ?? nayStara?.price ?? null;
  const prodazhna = Number(line.price || 0);
  const razlika = cena == null ? null : round2(prodazhna - cena);

  return (
    <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select className="small" value={line.batchId ?? ''}
        onChange={(e) => onPick(e.target.value)}
        style={{ maxWidth: 260 }}>
        <option value="">по реда на постъпване · {money(nayStara.price ?? 0)}</option>
        {batches.map((b) => (
          <option key={b.id} value={b.id}>
            {b.price == null ? 'без цена' : money(b.price)} · {quantity(b.quantity)} бр.
            {b.batchNo ? ` · ${b.batchNo}` : ''}
          </option>
        ))}
      </select>
      {razlika != null && (
        <span className={razlika > 0 ? 'muted' : 'error'} style={{ fontSize: 11 }}>
          {razlika > 0
            ? `надценка ${money(razlika)}`
            : `на загуба ${money(Math.abs(razlika))}`}
        </span>
      )}
    </div>
  );
}

/**
 * Трите цени под реда: към този клиент, изобщо и по картона.
 *
 * Показват се и трите нарочно. „Към клиента" е цената, която тръгва в
 * полето, но сама по себе си не казва дали е добра: може да е уговорена
 * преди година, докато доставната цена оттогава е мръднала. „Изобщо" и
 * „картон" дават мярката.
 *
 * Числото, с което цената съвпада, е получерно — така се вижда откъде
 * идва предложеното, без да се чете.
 */
/**
 * Разглеждане на стоката преди да влезе в продажбата.
 *
 * Показва всичко, което решава сделката: къде я има, от коя партида ще
 * излезе и на каква цена, срещу какво сме продавали досега. Количеството и
 * цената се въвеждат тук и редът влиза наготово.
 *
 * Досега беше обратното — редът лягаше в документа и чак тогава се виждаше
 * от коя партида излиза. При тънка надценка това значи, че решението се
 * взема, след като вече е взето.
 */
/** Едно ниво на цените се назовава с думата, с която го наричат в къщата. */
function priceLevelLabel(level) {
  return level === 'wholesale' ? 'на едро' : 'на дребно';
}

/**
 * Един ред от списъка с партиди.
 *
 * Цената е първото, което трябва да се види, затова стои отпред и в едър
 * шрифт. Избраната се откроява с рамка, а не само с фон — фонът се губи
 * на светъл екран.
 */
function BatchRow({ izbrana, pulna, onPick, cena, tekst }) {
  return (
    <button type="button" onClick={onPick} disabled={pulna}
      title={pulna ? 'Партидата е изчерпана' : 'Продай от тази партида'}
      style={{
        display: 'flex', gap: 8, alignItems: 'center', textAlign: 'left',
        padding: '6px 10px', borderRadius: 6, cursor: pulna ? 'not-allowed' : 'pointer', width: '100%',
        /*
         * Избраната партида трябва да се различава от пръв поглед, не с
         * едва забележима сянка — плътен цветен фон и по-дебела рамка,
         * плюс отметка вляво, вместо само тънка рамка в цвета на текста.
         */
        border: izbrana ? '2px solid var(--accent)' : '1px solid var(--border)',
        background: izbrana ? 'var(--accent-soft)' : 'var(--surface)',
        color: 'var(--text)', font: 'inherit',
        opacity: pulna ? 0.55 : 1,
      }}>
      <span style={{ width: 14, textAlign: 'center', color: 'var(--accent)', fontWeight: 700 }}>
        {izbrana ? '✓' : ''}
      </span>
      <strong style={{ minWidth: 68, textAlign: 'right' }}>
        {cena == null ? 'без цена' : money(cena)}
      </strong>
      <span className={izbrana ? undefined : 'muted'}>{tekst}</span>
    </button>
  );
}

function ConsumablesEditModal({ serviceId, onSaved, onClose }) {
  const [propertyOptions, setPropertyOptions] = useState(null);
  useEffect(() => {
    api.get('/api/properties').then((props) => {
      const byName = Object.fromEntries(props.map((p) => [p.name, p.values]));
      setPropertyOptions({ printType: byName['Тип печат'] ?? [], color: byName['Цвят'] ?? [] });
    }).catch(() => setPropertyOptions({ printType: [], color: [] }));
  }, []);
  return (
    <div className="modal-backdrop">
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 760 }}>
        {propertyOptions
          ? <ServiceReview serviceId={serviceId} onSaved={onSaved} propertyOptions={propertyOptions} />
          : <div className="muted">Зареждане…</div>}
        <div className="modal-foot">
          <button type="button" onClick={onClose}>Затвори</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Тонер/чип, закачени към услугата (виж „Тонер/чип на услугите").
 *
 * Показва се само когато има какво да избере. При точно един вариант
 * той е предварително избран — нула клика в обичайния случай; при
 * повече от един операторът превключва тук, преди „Продай".
 */
function ConsumablesPicker({ gled, onChange }) {
  const { toners = [], chips = [] } = gled.consumables ?? {};
  const [redakcia, setRedakcia] = useState(false);
  const eZarezhdane = /зареждане/i.test(gled.info?.product?.group_name ?? '');

  /*
   * Редакция на закачените тонер/чип направо оттук (09.10.2026) — същият
   * редактор като в „Тонер/чип на услугите". След запис изборът се
   * презарежда, без да се губи въведеното по реда.
   */
  async function sledZapis() {
    const fresh = await api.get(withQuery(`/api/service-consumables/${gled.id}/for-sale`, {
      warehouseId: gled.warehouseId ? Number(gled.warehouseId) : undefined,
    })).catch(() => ({ toners: [], chips: [] }));
    onChange({
      consumables: fresh,
      chosenTonerId: fresh.toners.some((t) => t.productId === gled.chosenTonerId)
        ? gled.chosenTonerId : (fresh.toners[0]?.productId ?? null),
      chosenChipId: fresh.chips.some((c) => c.productId === gled.chosenChipId)
        ? gled.chosenChipId : null,
    });
  }

  const prozorec = redakcia && (
    <ConsumablesEditModal serviceId={gled.id} onSaved={sledZapis} onClose={() => setRedakcia(false)} />
  );

  if (toners.length === 0 && chips.length === 0) {
    if (!eZarezhdane) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <button type="button" className="link" onClick={() => setRedakcia(true)}>
          закачи тонер/чип към услугата
        </button>
        {prozorec}
      </div>
    );
  }

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 8,
      padding: '8px 10px', marginBottom: 14, background: 'var(--surface-2)',
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <div className="muted" style={{ fontSize: 11 }}>
          Тонер/чип за зареждането — ще влязат на отделен ред, като подарък
        </div>
        <button type="button" className="link" style={{ fontSize: 12 }} onClick={() => setRedakcia(true)}>
          редакция
        </button>
      </div>
      {prozorec}
      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        {toners.length > 0 && (
          <div>
            <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>Тонер</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {toners.map((t) => (
                <label key={t.productId} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="radio"
                    name={`toner-${gled.id}`}
                    checked={gled.chosenTonerId === t.productId}
                    onChange={() => onChange({ chosenTonerId: t.productId })}
                  />
                  {t.name} {t.grams ? <span className="muted">({t.grams}гр.)</span> : null}
                  <Nalichno c={t} />
                </label>
              ))}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="radio"
                  name={`toner-${gled.id}`}
                  checked={!gled.chosenTonerId}
                  onChange={() => onChange({ chosenTonerId: null })}
                />
                <span className="muted">без тонер</span>
              </label>
            </div>
          </div>
        )}
        {chips.length > 0 && (
          <div>
            <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>Чип / зануляване</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {chips.map((c) => (
                <label key={c.productId} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="radio"
                    name={`chip-${gled.id}`}
                    checked={gled.chosenChipId === c.productId}
                    onChange={() => onChange({ chosenChipId: c.productId })}
                  />
                  {c.name}
                  <Nalichno c={c} />
                </label>
              ))}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="radio"
                  name={`chip-${gled.id}`}
                  checked={!gled.chosenChipId}
                  onChange={() => onChange({ chosenChipId: null })}
                />
                <span className="muted">без чип</span>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/*
 * Колко остава във всяка партида след редовете, вече сложени на екрана
 * (09.10.2026). Изчерпаната партида изчезва от избора, за да може същата
 * стока да се изпише на нов ред от следващата. Редовете „по реда на
 * постъпване" (без партида) се приспадат от най-старите напред — така
 * ще ги изпише и сървърът. При редакция се брои само добавеното.
 */
function ostatakPoPartidi(batches, lines, productId) {
  const vzeto = {};
  let fifo = 0;
  for (const l of lines ?? []) {
    if (Number(l.productId) !== Number(productId)) continue;
    const kol = l.dbId
      ? Number(l.quantity || 0) - Number(l.origQty ?? l.quantity ?? 0)
      : Number(l.quantity || 0);
    if (!kol) continue;
    if (l.batchId) vzeto[l.batchId] = (vzeto[l.batchId] ?? 0) + kol;
    else fifo += kol;
  }
  const out = (batches ?? []).map((b) => ({ ...b, quantity: Number(b.quantity) - (vzeto[b.id] ?? 0) }));
  for (const b of out) {
    if (fifo <= 0) break;
    const t = Math.min(fifo, Math.max(b.quantity, 0));
    b.quantity -= t;
    fifo -= t;
  }
  return out.filter((b) => b.quantity > 0.00005);
}

function SalePanel({ gled, lines, onChange, onSell, onClose }) {
  const kolvo = useRef(null);
  const panel = useRef(null);

  // Курсорът влиза в количеството веднага — оттам нататък е само писане.
  // Панелът се изтегля в полезрението: отваря се под търсачката и без
  // това долният му край остава под ръба на екрана.
  useEffect(() => {
    if (gled.loading) return;
    kolvo.current?.select();
    panel.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [gled.loading, gled.id]);

  if (gled.loading) {
    return <div className="card"><div className="empty">Зареждане на {gled.name}…</div></div>;
  }

  const { product, stock, prices } = gled.info;
  const batches = ostatakPoPartidi(gled.info.batches, lines, gled.id);
  const kolichestvo = Number(gled.quantity || 0);
  const cena = Number(gled.price || 0);
  const stavka = 1 + Number(product.vat_rate || 0) / 100;

  /*
   * Двете цени се пишат и в двете посоки.
   *
   * Работим без ДДС, но на гишето клиентът пита „колко струва" и отговорът
   * е с ДДС. Оператор, който знае колко да вземе, го въвежда направо,
   * вместо да го дели наум.
   *
   * Водеща е цената без ДДС — тя влиза в документа. Другата е нейно
   * отражение и се пази отделно само докато се пише в нея: иначе всяка
   * буква щеше да се преизчислява напред-назад и полето да подскача.
   */
  const bezDds = (x) => (x === '' ? '' : String(x));
  const sDdsPokazana = gled.priceGross != null
    ? gled.priceGross
    : (gled.price === '' ? '' : (Number(gled.price || 0) * stavka).toFixed(2));

  /*
   * Цена по ценовите правила — стълба по количество (services/pricing.js).
   *
   * Докато операторът не е пипнал цената сам, тя следва бройките: мине ли
   * прагът „от 15 бр.", цената слиза сама; падне ли под всички прагове —
   * връща се на последната към клиента, иначе на картона. Пипната на
   * ръка (или избрана с клик от „Цени от продажби") — остава, каквато е.
   */
  const stalba = prices.rules ?? [];
  const stapaloZa = (q) => {
    let hit = null;
    for (const s of stalba) if (s.fromQty <= Number(q || 0) + 1e-9) hit = s;
    return hit?.ruleId ? hit : null;
  };
  const bezPravilo = prices.client?.price ?? prices.card;
  const segashno = stapaloZa(kolichestvo);
  const sledvashto = stalba.find((s) => s.ruleId && s.fromQty > kolichestvo + 1e-9) ?? null;
  const sKolichestvo = (patch) => {
    if (gled.priceManual || stalba.length === 0) return onChange(patch);
    const s = stapaloZa(patch.quantity);
    const nova = s ? s.price : bezPravilo;
    return onChange({
      ...patch,
      ...(nova == null ? {} : { price: cenaVPole(nova), priceGross: null }),
    });
  };

  const pishiBez = (v) => onChange({ price: v, priceGross: null, priceManual: true });
  const pishiS = (v) => onChange({
    priceGross: v,
    price: v === '' ? '' : String(Math.round((Number(v) / stavka) * 10000) / 10000),
    priceManual: true,
  });
  /*
   * Кликване по партида добавя бройка, не я избира.
   *
   * Операторът вади стоката физически от рафта партида по партида — щрака
   * толкова пъти, колкото взима от всяка. Когато една свърши, кликва
   * следващата и продажбата излиза от две партиди наведнъж. „По реда на
   * постъпване" прави същото само автоматично: взима от най-старата, която
   * още има какво да даде.
   *
   * Ръчно въведено количество зачерква тази разбивка — тръгва отначало,
   * по реда на постъпване за цялото число, както преди тази възможност.
   */
  const alloc = gled.batchQty ?? {};
  const vzeto = (id) => Number(alloc[id] ?? 0);

  /*
   * Доставната цена следва кликнатото, не старото поле `batchId`.
   *
   * От кликването по партиди насам изборът е разбивка (`batchQty`), не
   * едно име — щрака се по няколко партиди и всяка носи своя доставна
   * цена. Надценката трябва да гледа среднопретеглената от наистина
   * взетото, иначе показва разход от партида, от която нищо не излиза, и
   * лъже с привидна загуба.
   */
  const alocIma = Object.keys(alloc).length > 0;
  const dostavna = alocIma
    ? (() => {
      let suma = 0;
      let broyki = 0;
      for (const [bId, kol] of Object.entries(alloc)) {
        const b = batches.find((x) => String(x.id) === String(bId));
        const cenaNaPartidata = b?.price ?? product.purchase_price;
        if (cenaNaPartidata == null || !kol) continue;
        suma += cenaNaPartidata * kol;
        broyki += Number(kol);
      }
      return broyki > 0 ? round2(suma / broyki) : (batches[0]?.price ?? product.purchase_price ?? null);
    })()
    : (batches[0]?.price ?? product.purchase_price ?? null);
  const razlika = dostavna == null ? null : round2(cena - dostavna);
  const dobavi = (novRazbivka) => {
    const obshto = Object.values(novRazbivka).reduce((a, n) => a + n, 0);
    sKolichestvo({ batchQty: novRazbivka, quantity: String(obshto), batchId: '' });
  };
  /*
   * Една партида на ред (09.10.2026, Калин). Клик по партида я избира
   * вместо досегашната — не добавя бройка към нея и не трупа втора. Ако
   * количеството е повече от наличното в партидата, остава колкото има;
   * останалото се продава на нов ред, от друга партида.
   */
  const kliknatoPoPartida = (b) => {
    const iskano = kolichestvo > 0 ? kolichestvo : 1;
    dobavi({ [b.id]: Math.min(iskano, Number(b.quantity)) });
  };
  const kliknatoPoRed = () => {
    sKolichestvo({ batchQty: null, batchId: '' });
  };
  const edinstvena = Object.keys(alloc).length === 1
    ? batches.find((x) => String(x.id) === Object.keys(alloc)[0]) : null;
  /*
   * Наличността се брои само в склада, от който наистина продаваме.
   * Сборът от всички складове тук би бил грешен — стока с 23 бр. във
   * Варна и 18 бр. в Лайнян склад показва "налично 23" на реда (Варна е
   * складът по документа), а панелът смяташе 41 — двете number-а не бива
   * да си противоречат.
   */
  const naSklad = stock.reduce((a, r) => a
    + (Number(r.warehouse_id) === Number(gled.warehouseId) ? Number(r.quantity) : 0), 0);
  const stiga = !product.is_stocked || kolichestvo <= naSklad;

  // Избраната партида остава, докато в нея има толкова.
  const pishiKolichestvo = (stojnost) => {
    const n = Number(String(stojnost).replace(',', '.'));
    sKolichestvo({
      quantity: stojnost,
      batchQty: edinstvena && n > 0 && n <= Number(edinstvena.quantity)
        ? { [edinstvena.id]: n } : null,
    });
  };

  const cenaRed = (etiket, stojnost, dop) => (stojnost == null ? null : (
    <button type="button" className="link" style={{ textAlign: 'left' }}
      onClick={() => onChange({ price: cenaVPole(stojnost), priceGross: null, priceManual: true })}
      title="Сложи тази цена">
      {etiket} <strong>{money(stojnost)}</strong>
      {dop && <span className="muted"> {dop}</span>}
    </button>
  ));

  return (
    <div className="card" ref={panel} style={{ marginTop: 12 }}>
      <div className="card-head">
        {product.name}
        {product.code && <span className="muted"> · {product.code}</span>}
        <span style={{ flex: 1 }} />
        <button type="button" className="link" onClick={onClose}>затвори</button>
      </div>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end',
          flexWrap: 'wrap', paddingBottom: 14, marginBottom: 14,
          borderBottom: '1px solid var(--border)' }}>
          {/* Двете полета, които наистина влизат в документа, са с фон.
              Цената с ДДС до тях е само помощна. */}
          <label className="field" style={{ maxWidth: 150 }}>
            Количество
            {/* Стрелките в полето местят с 1 цяло, като при етикетите
                (03.11.2026). Ръчно може и дробно (напр. 0,5). Колелцето на
                мишката не мени нищо — полето губи фокуса (виж NumBox.jsx). */}
            <input ref={kolvo} className="num vhod" type="number" step="1" min="1"
              value={gled.quantity}
              onChange={(e) => pishiKolichestvo(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              onKeyDown={(e) => { if (e.key === 'Enter') onSell(); }} />
          </label>
          <label className="field" style={{ maxWidth: 140 }}>
            Ед. цена без ДДС
            <input className="num vhod" value={bezDds(gled.price)}
              onChange={(e) => pishiBez(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSell(); }} />
          </label>
          <label className="field" style={{ maxWidth: 140 }}>
            Ед. цена с ДДС
            <input className="num" value={sDdsPokazana}
              onChange={(e) => pishiS(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSell(); }} />
          </label>

          <div className="field">
            Сума без ДДС
            <div className="field-static"><strong>{money(round2(kolichestvo * cena))}</strong></div>
          </div>
          {/* Клиентът плаща това число — само за сведение, в документа
              влиза основата. */}
          <div className="field">
            Сума с ДДС
            <div className="field-static muted">
              {money(round2(kolichestvo * cena * stavka))}
            </div>
          </div>

          {/* Надценката е причината панелът да съществува: при тънък марж
              изборът на партида решава дали сделката е на печалба. */}
          {razlika != null && (
            <div className="field">
              Надценка
              <div className={`field-static ${razlika < 0 ? 'error' : ''}`}>
                {razlika < 0 ? `−${money(Math.abs(razlika))}` : money(razlika)}
                <span className="muted" style={{ fontSize: 11 }}>
                  {' '}над {money(dostavna)}
                </span>
              </div>
            </div>
          )}

          <div style={{ flex: 1 }} />
          {!stiga && (
            <span className="error" style={{
              padding: '4px 9px', borderRadius: 4, fontWeight: 700,
              background: 'color-mix(in srgb, var(--danger) 14%, var(--surface))',
            }}>
              няма толкова наличност — на склад има {quantity(naSklad)} {product.unit}
            </span>
          )}
          {/*
            * Бележка за конкретния ред — излиза на фактурата.
            *
            * Клиентът иска да види кое за кой отдел е: две касети към една
            * фирма, едната за личен състав, другата за счетоводството.
            * Написаното се пренася и на следващата стока, защото обикновено
            * цялата доставка е за същия отдел; изтрива се с едно избърсване.
            */}
          <label className="field" style={{ minWidth: 220, flex: '0 1 320px' }}>
            Бележка за реда
            <input value={gled.note ?? ''}
              placeholder="напр. за счетоводството"
              onChange={(e) => onChange({ note: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') onSell(); }} />
          </label>
          {/* Подарък (20.10.2026) — резервните части често се дават без пари;
              отметката преди „Продай" пести поправката на реда след това. */}
          <label style={{
            display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 9,
            fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            <input type="checkbox" checked={!!gled.isGift}
              onChange={(e) => onChange({ isGift: e.target.checked })}
              style={{ width: 18, height: 18 }} />
            Подарък
          </label>
          <button type="button" className="primary" onClick={() => {
              /*
               * Блокира се тук, не чак на "Запис".
               *
               * Иначе операторът добавя реда с жълт бадж "налично 23",
               * продължава да въвежда остатъка от документа, и чак накрая
               * разбира от посивяло копче "Запис", че този ред трябва да
               * се поправи — твърде късно, за да е удобно.
               */
              if (!stiga) {
                window.alert(
                  `Няма толкова наличност.\n\n„${product.name}" в склада има `
                  + `${quantity(naSklad)} ${product.unit}, а искате да продадете `
                  + `${quantity(kolichestvo)} ${product.unit}.`);
                return;
              }
              onSell();
            }}
            disabled={!(kolichestvo > 0)}>
            Продай
          </button>
        </div>

        <ConsumablesPicker gled={gled} onChange={onChange} />

        {/*
          * Две колони, не един общ ред с пренасяне.
          *
          * Партидата е първото, което операторът трябва да реши — под
          * количеството, вляво, не изтикана някъде накрая на реда. С
          * фиксирана лява колона позицията ѝ не зависи от дължината на
          * съседния текст („последна изобщо" може да е дълго заради
          * името на клиента).
          */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: batches.length > 0 ? '280px 1fr' : '1fr',
          gap: 24,
          alignItems: 'start',
        }}
        >
          {batches.length > 0 && (
            <div style={{
              border: '1px solid var(--border)', borderRadius: 8,
              padding: '8px 10px', background: 'var(--surface-2)',
            }}
            >
              <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
                Партида, от която продаваме
              </div>
              {/* Списък, а не падащо меню: цените трябва да се сравнят с
                  един поглед, а падащото ги показва едва след отваряне.
                  Всеки ред е бутон „+1 бройка от тази партида" — не избор
                  веднъж завинаги. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <BatchRow
                  izbrana={Object.keys(alloc).length === 0}
                  onPick={kliknatoPoRed}
                  cena={batches[0].price}
                  tekst="по реда на постъпване"
                />
                {/* Списъкът тук е за избор от оператора — най-новата
                    партида най-отгоре, по-лесно се намира. FIFO-то на
                    „по реда на постъпване" отгоре не се пипа — то си
                    гледа `batches` в оригиналния (най-стара напред) ред. */}
                {[...batches].reverse().map((b) => (
                  <BatchRow
                    key={b.id}
                    izbrana={vzeto(b.id) > 0}
                    pulna={false}
                    onPick={() => kliknatoPoPartida(b)}
                    cena={b.price}
                    tekst={<>
                      <strong style={{ color: 'var(--accent)' }}>
                        {quantity(b.quantity)} {product.unit}
                      </strong>
                      {b.batchNo ? ` · ${b.batchNo}` : ''}
                      {b.supplier && <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.25 }}>{b.supplier}</div>}
                    </>}
                  />
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>

          <div style={{ minWidth: 230 }}>
            <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>Наличност</div>
            {/* Услугата няма склад (30.10.2026, з1030) — нито „няма никъде“, нито нули. */}
            {product.is_stocked != null && !Number(product.is_stocked) ? <div><span className="badge">услуга</span> <span className="muted">без наличност</span></div>
              : stock.length === 0
              ? <div className="error">няма никъде</div>
              : stock.map((r) => {
                // Складът, от който продаваме, е в едър шрифт; другите са
                // само за сведение — стоката там не участва в тази продажба.
                const tozi = Number(r.warehouse_id) === Number(gled.warehouseId);
                return (
                  <div key={r.warehouse_id} style={{
                    display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 2,
                  }}>
                    <span style={{
                      fontSize: tozi ? 20 : 14, fontWeight: tozi ? 600 : 400,
                      opacity: tozi ? 1 : 0.6, minWidth: 62, textAlign: 'right',
                    }}>
                      {quantity(r.quantity)}
                    </span>
                    <span className={tozi ? '' : 'muted'}>
                      {product.unit} · {r.warehouse_name}
                    </span>
                  </div>
                );
              })}
          </div>

          {/* Каталожното наименование не казва каква е стоката. Групата,
              описанието и капацитетът я казват — затова стоят на видно
              място, а не сбити под наличността. */}
          <div style={{ minWidth: 230, maxWidth: 340 }}>
            <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>Каква е стоката</div>
            {product.group_name && <div>{product.group_name}</div>}
            {product.note && <div style={{ marginTop: 2 }}>{product.note}</div>}
            {product.properties_text && (
              <div style={{ marginTop: 4, fontWeight: 600 }}>{product.properties_text}</div>
            )}
            {!product.group_name && !product.note && !product.properties_text && (
              <span className="muted">няма описание</span>
            )}
          </div>

          <div style={{ minWidth: 250, maxWidth: 320 }}>
            {stalba.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
                  По ценово правило
                  {gled.priceManual && <span> · цената е сменена на ръка</span>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {segashno
                    ? cenaRed('сега', segashno.price, segashno.label)
                    : <span className="muted" style={{ fontSize: 11 }}>при това количество не важи</span>}
                  {sledvashto && (
                    <span style={{ fontSize: 12 }}>
                      при <strong>{Number(sledvashto.fromQty).toLocaleString('bg-BG')} бр.</strong>
                      {' '}— <strong>{money(sledvashto.price)}</strong>
                      <span className="muted"> · още {Math.max(0, sledvashto.fromQty - kolichestvo).toLocaleString('bg-BG')} бр.</span>
                    </span>
                  )}
                </div>
              </div>
            )}
            {/*
              * Всички цени на едно място (07.10.2026) — таблица, клик по
              * ред слага цената в продажбата. Текущата е оцветена.
              */}
            <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>Цени — клик избира</div>
            <table className="compact ceni-tabl" style={{ width: '100%' }}>
              <thead>
                <tr><th /><th className="num">без ДДС</th><th className="num">с ДДС</th></tr>
              </thead>
              <tbody>
                {[
                  segashno && ['по ценово правило', segashno.price, segashno.label],
                  ['към клиента', prices.client?.price,
                    prices.client && `${date(prices.client.date)}${
                      gled.methodName?.[prices.client.paymentMethod]
                        ? ` · ${gled.methodName[prices.client.paymentMethod]}` : ''}`],
                  ['последна изобщо', prices.any?.price,
                    prices.any && `${date(prices.any.date)}${
                      prices.any.partnerName ? ` · ${prices.any.partnerName}` : ''}`],
                  ['дребно', product.retail_price, 'от картона'],
                  ['едро', product.wholesale_price, 'от картона'],
                  ['доставна', dostavna ?? product.purchase_price,
                    batches.length > 0 ? 'от партидите' : 'от картона'],
                ].filter(Boolean).map(([etiket, cena, dop]) => {
                  const ima = cena != null && Number(cena) > 0;
                  const izbrana = ima && Math.abs(Number(gled.price) - Number(cena)) < 0.00005;
                  return (
                    <tr key={etiket}
                      className={izbrana ? 'open' : (ima ? 'clickable' : '')}
                      style={{ cursor: ima ? 'pointer' : 'default', opacity: ima ? 1 : 0.5 }}
                      title={ima ? 'Сложи тази цена' : undefined}
                      onClick={() => ima && onChange({ price: cenaVPole(cena), priceGross: null, priceManual: true })}>
                      <td>
                        {etiket}
                        {dop && <div className="muted" style={{ fontSize: 10.5, lineHeight: 1.2 }}>{dop}</div>}
                      </td>
                      <td className="num"><strong>{ima ? money(cena) : '—'}</strong></td>
                      <td className="num muted">{ima ? money(Number(cena) * stavka) : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          </div>
        </div>

      </div>
    </div>
  );
}

/**
 * Разглеждане на стоката преди да влезе в доставката.
 *
 * Огледално на SalePanel, но по-просто: доставката не избира от коя
 * партида излиза стоката — тя винаги отваря своя нова. Партидите тук
 * стоят само за сведение (има ли вече наличност, на каква цена), заедно
 * с историята на предишни доставки от този и от други доставчици.
 */
function PurchasePanel({ gled, onChange, onAdd, onClose }) {
  const kolvo = useRef(null);
  const panel = useRef(null);

  useEffect(() => {
    if (gled.loading) return;
    kolvo.current?.select();
    panel.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [gled.loading, gled.id]);

  if (gled.loading) {
    return <div className="card"><div className="empty">Зареждане на {gled.name}…</div></div>;
  }

  const { product, stock } = gled.info;
  // Изчерпаните партиди са само шум тук — редът и без това отваря нова.
  const batches = gled.info.batches.filter((b) => Number(b.quantity) > 0);
  const p = gled.product;
  const kolichestvo = Number(gled.quantity || 0);
  const cena = Number(gled.price || 0);
  const stavka = 1 + Number(p.vat_rate || 0) / 100;

  const bezDds = (x) => (x === '' ? '' : String(x));
  const sDdsPokazana = gled.priceGross != null
    ? gled.priceGross
    : (gled.price === '' ? '' : (Number(gled.price || 0) * stavka).toFixed(2));
  const pishiBez = (v) => onChange({ price: v, priceGross: null });
  const pishiS = (v) => onChange({
    priceGross: v,
    price: v === '' ? '' : String(Math.round((Number(v) / stavka) * 10000) / 10000),
  });

  const naSklad = stock.reduce((a, r) => a
    + (Number(r.warehouse_id) === Number(gled.warehouseId) ? Number(r.quantity) : 0), 0);

  return (
    <div className="card" ref={panel} style={{ marginTop: 12 }}>
      <div className="card-head">
        {p.name}
        {p.code && <span className="muted"> · {p.code}</span>}
        <span style={{ flex: 1 }} />
        <button type="button" className="link" onClick={onClose}>затвори</button>
      </div>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end',
          flexWrap: 'wrap', paddingBottom: 14, marginBottom: 14,
          borderBottom: '1px solid var(--border)' }}>
          <label className="field" style={{ maxWidth: 150 }}>
            Количество
            {/* Доставката винаги е на цели бройки — стрелките местят с 1,
                без риск от случайно завъртяно колелце на мишката (затова
                не е type="number" — виж NumBox.jsx). */}
            <div style={{ display: 'flex', gap: 4 }}>
              <button type="button" title="с 1 по-малко" style={{ padding: '7px 10px' }}
                onClick={() => onChange({
                  quantity: String(Math.max(1, (parseInt(gled.quantity, 10) || 0) - 1)),
                })}>
                −
              </button>
              <input ref={kolvo} className="num vhod" style={{ textAlign: 'center' }}
                value={gled.quantity}
                onChange={(e) => onChange({ quantity: e.target.value.replace(/[^\d]/g, '') })}
                onKeyDown={(e) => { if (e.key === 'Enter') onAdd(); }} />
              <button type="button" title="с 1 повече" style={{ padding: '7px 10px' }}
                onClick={() => onChange({
                  quantity: String((parseInt(gled.quantity, 10) || 0) + 1),
                })}>
                +
              </button>
            </div>
          </label>
          <label className="field" style={{ maxWidth: 140 }}>
            Ед. цена без ДДС
            <input className="num vhod" value={bezDds(gled.price)}
              onChange={(e) => pishiBez(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onAdd(); }} />
          </label>
          <label className="field" style={{ maxWidth: 140 }}>
            Ед. цена с ДДС
            <input className="num" value={sDdsPokazana}
              onChange={(e) => pishiS(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onAdd(); }} />
          </label>
          <div className="field">
            Сума без ДДС
            <div className="field-static"><strong>{money(round2(kolichestvo * cena))}</strong></div>
          </div>
          <div className="field">
            Сума с ДДС
            <div className="field-static muted">
              {money(round2(kolichestvo * cena * stavka))}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <label className="field" style={{ minWidth: 220, flex: '0 1 320px' }}>
            Бележка за реда
            <input value={gled.note ?? ''}
              placeholder="напр. за счетоводството"
              onChange={(e) => onChange({ note: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') onAdd(); }} />
          </label>
          <button type="button" className="primary" onClick={onAdd}
            disabled={!(kolichestvo > 0)}>
            Добави
          </button>
        </div>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {batches.length > 0 && (
            <div style={{
              border: '1px solid var(--border)', borderRadius: 8,
              padding: '8px 10px', background: 'var(--surface-2)', minWidth: 260,
            }}
            >
              <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
                Съществуващи партиди — само за сведение, редът отваря нова
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {batches.map((b) => (
                  <div key={b.id} style={{
                    display: 'flex', gap: 8, alignItems: 'center', padding: '4px 2px',
                  }}>
                    <strong style={{ minWidth: 68, textAlign: 'right' }}>
                      {b.purchase_price == null ? 'без цена' : money(b.purchase_price)}
                    </strong>
                    <span className="muted">
                      {quantity(b.quantity)} {product.unit} · {b.warehouse_name}
                      {b.batch_no ? ` · ${b.batch_no}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ minWidth: 230 }}>
            <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>Наличност</div>
            {/* Услугата няма склад (30.10.2026, з1030) — нито „няма никъде“, нито нули. */}
            {product.is_stocked != null && !Number(product.is_stocked) ? <div><span className="badge">услуга</span> <span className="muted">без наличност</span></div>
              : stock.length === 0
              ? <div className="error">няма никъде</div>
              : stock.map((r) => {
                const tozi = Number(r.warehouse_id) === Number(gled.warehouseId);
                return (
                  <div key={r.warehouse_id} style={{
                    display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 2,
                  }}>
                    <span style={{
                      fontSize: tozi ? 20 : 14, fontWeight: tozi ? 600 : 400,
                      opacity: tozi ? 1 : 0.6, minWidth: 62, textAlign: 'right',
                    }}>
                      {quantity(r.quantity)}
                    </span>
                    <span className={tozi ? '' : 'muted'}>
                      {product.unit} · {r.warehouse_name}
                    </span>
                  </div>
                );
              })}
            {naSklad === 0 && stock.length > 0 && (
              <span className="muted" style={{ fontSize: 11 }}>
                няма в {gled.warehouseId ? 'този склад' : 'избрания склад'}
              </span>
            )}
          </div>

          <div style={{ minWidth: 250, maxWidth: 340 }}>
            <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>Предишни доставки</div>
            <LastPurchases hist={gled.hist} now={cena} />
            {!gled.hist?.same && !gled.hist?.other && !gled.hist?.test && (
              <span className="muted" style={{ fontSize: 11 }}>няма предишна доставка</span>
            )}
            {(p.note || p.properties_text) && (
              <>
                <div className="muted" style={{ fontSize: 11, margin: '10px 0 4px' }}>
                  Каква е стоката
                </div>
                {p.note && <div style={{ fontSize: 12 }}>{p.note}</div>}
                {p.properties_text && (
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{p.properties_text}</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Какво описва реда: каталожен номер, капацитет, бележки.
 *
 * Наименованието е каталожен код и само по себе си не казва каква е
 * стоката. Бележката за реда е другото важно — тя излиза на фактурата и
 * казва за кого е („за счетоводството").
 */
function LineInfo({ line }) {
  const parcheta = [line.productCode, line.properties].filter(Boolean);
  // Бележката за реда е в полето под реда (редактируема) — тук не се повтаря.
  if (parcheta.length === 0 && !line.productNote2) return null;

  return (
    <div style={{ marginTop: 2 }}>
      {parcheta.length > 0 && (
        <div className="muted" style={{ fontSize: 11 }}>{parcheta.join(' · ')}</div>
      )}
      {line.productNote2 && (
        <div className="muted" style={{ fontSize: 11 }}>{line.productNote2}</div>
      )}
    </div>
  );
}

function LastSales({ line, now }) {
  const h = line.saleHist;
  const karton = line.cardPrice == null ? null : Number(line.cardPrice);
  if (!h?.client && !h?.any && karton == null) return null;

  const eднакво = (x) => x != null && Math.abs(Number(x) - now) < 0.005;

  const parche = (etiket, cena, dop) => (cena == null ? null : (
    <span className="muted" style={{ fontSize: 11 }}>
      {etiket}{' '}
      {eднакво(cena) ? <strong>{money(cena)}</strong> : money(cena)}
      {dop && <span style={{ opacity: 0.7 }}> {dop}</span>}
    </span>
  ));

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 2 }}>
      {parche('към клиента', h?.client?.price, h?.client && date(h.client.date))}
      {parche('последна изобщо', h?.any?.price,
        h?.any && `${date(h.any.date)}${h.any.partnerName ? ` · ${h.any.partnerName}` : ''}`)}
      {parche('картон', karton)}
    </div>
  );
}

function LastPurchases({ hist, now }) {
  if (!hist?.same && !hist?.other && !hist?.test) return null;

  const row = (label, h, extra) => {
    if (!h) return null;
    const was = Number(h.price);
    const diff = now > 0 && was > 0 ? ((now - was) / was) * 100 : 0;
    const moved = Math.abs(diff) >= 0.5;
    return (
      <div className="muted" style={{ fontSize: 11 }}>
        {label}: <strong>{money(h.price)}</strong> на {date(h.date)}
        {extra}
        {moved && (
          <span style={{ marginLeft: 6, color: diff > 0 ? 'var(--danger)' : 'var(--ok)' }}>
            {diff > 0 ? '↑' : '↓'} {Math.abs(diff).toFixed(0)}%
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      {row('последно от този доставчик', hist.same)}
      {row('от друг', hist.other,
        hist.other?.partnerName ? <span> — {hist.other.partnerName}</span> : null)}
      {row(
        'пробна доставка',
        hist.test,
        <>
          {hist.test?.partnerName ? <span> — {hist.test.partnerName}</span> : null}
          <span className="badge" style={{ marginLeft: 6 }}>проба</span>
        </>
      )}
    </>
  );
}

/** Търсене на стока в самия ред — за разчетени редове без съвпадение. */
function LinePicker({ line, partnerId, isPurchase = true, onPick, onCancel }) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [making, setMaking] = useState(false);

  /*
   * Текстът на реда пътува с търсенето.
   *
   * Без него подредбата е само по близост на буквите и по «4020» излизат
   * сухо лепило, барабанен модул и зареждане, а тонер касетата остава
   * извън видимите. С него сървърът гледа какво КАЗВА редът: вид, марка
   * и брой копия. Виж `createLineRanker`.
   */
  useEffect(() => {
    if (query.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(() => {
      api.get(withQuery('/api/products', {
        // Услугите са вън само при доставка. При продажба от „Чакат
        // продажба" несвързаният ред е точно услуга („з3140") — 28.10.2026.
        q: query.trim(), limit: 8, activeOnly: false, excludeServices: isPurchase || undefined,
        lineText: line?.supplierText || undefined,
        partnerId: partnerId || undefined,
        withLastPurchase: isPurchase || undefined,
      })).then((r) => setHits(r.items)).catch(() => setHits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [query, line?.supplierText, partnerId, isPurchase]);

  return (
    <div style={{ position: 'relative' }}>
      <input type="search" autoComplete="off" autoFocus={!!onCancel} style={{ width: '100%' }}
        placeholder="посочи нашата стока"
        value={query} onChange={(e) => setQuery(e.target.value)} />
      {onCancel ? (
        <button className="link" style={{ marginTop: 4 }} onClick={onCancel}>откажи</button>
      ) : (
        <span className="badge danger" style={{ marginTop: 4 }}>несвързан</span>
      )}
      {/* Винаги видим (13.10.2026) — не само когато търсенето е празно:
          близки по код стоки излизат и когато точната липсва. */}
      {!making && (
        <button type="button" style={{ marginTop: 4, marginLeft: 10, padding: '2px 10px' }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { setHits([]); setMaking(true); }}>
          + нова стока
        </button>
      )}
      {making && (
        <NewProduct
          name={line.description ?? query}
          onDone={(p) => { setMaking(false); setQuery(''); setHits([]); onPick(p); }}
          onCancel={() => setMaking(false)}
        />
      )}
      {hits.length > 0 && !making && (
        <Suggestions
          items={hits}
          render={(p) => (
            <>
              <strong>{p.name}</strong> <span className="muted">{p.group_name}</span>
              {/* Наличността решава коя от близките стоки е търсената —
                  тази, от която има, обикновено е тя. */}
              {/* Услугата няма наличност — „няма" до нея лъже (28.10.2026). */}
              {Number(p.is_stocked) === 0 ? (
                <span className="badge" style={{ marginLeft: 8 }}>услуга</span>
              ) : (
                <span className={Number(p.quantity) > 0 ? 'badge' : 'badge muted'}
                  style={{ marginLeft: 8 }}>
                  {Number(p.quantity) > 0 ? `налични ${quantity(p.quantity)}` : 'няма'}
                </span>
              )}
              {/* Две касети за един и същ принтер се различават само по
                  капацитет — без него изборът е налучкване. */}
              {p.properties_text && (
                <div className="muted" style={{ fontSize: 11 }}>{p.properties_text}</div>
              )}
              {p.note && (
                <div className="muted" style={{
                  fontSize: 11, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{p.note}</div>
              )}
              <PoslednaDostavka p={p} />
            </>
          )}
          onPick={(p) => { setQuery(''); setHits([]); onPick(p); }}
        />
      )}
    </div>
  );
}

/**
 * Създаване на стока направо от реда на фактурата.
 *
 * Иначе човекът трябва да излезе от доставката, да отвори
 * номенклатурата, да създаде стоката и да започне отначало — а редът,
 * който е разчитал, дотогава е забравен.
 *
 * Задължителни са група и наименование; цените, описанието за фактурата
 * и характеристиките се попълват веднага по желание (07.10.2026).
 */
/*
 * Падащи менюта вместо свободен текст (13.10.2026) — за да се пишат винаги
 * еднакво. Записва се досегашната стойност (Black/Cyan/…), защото по нея
 * консумативите на услугите търсят пасващ тонер/чип
 * (serviceConsumables.js); на екрана излиза на български.
 */
const IZBOR_SVOISTVO = {
  'Тип печат': [['Монохромен', 'Монохромен'], ['Цветен', 'Цветен']],
  'Цвят': [
    ['Монохромен', 'Монохромен'], ['Black', 'Черен (Black)'], ['Magenta', 'Червен (Magenta)'],
    ['Cyan', 'Син (Cyan)'], ['Yellow', 'Жълт (Yellow)'],
  ],
};

function NewProduct({ name, onDone, onCancel }) {
  /*
   * Пълен картон и тук (07.10.2026) — цени, описание за фактурата и
   * характеристики, както „Нова стока“ в Наличности. Задължителни остават
   * само група и наименование.
   */
  const [groups, setGroups] = useState([]);
  const [properties, setProperties] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [title, setTitle] = useState(name ?? '');
  const [code, setCode] = useState('');
  const [unit, setUnit] = useState('бр.');
  const [barcode, setBarcode] = useState('');
  const [vatRate, setVatRate] = useState('20');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [wholesalePrice, setWholesalePrice] = useState('');
  const [retailPrice, setRetailPrice] = useState('');
  const [note, setNote] = useState('');
  const [props, setProps] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/api/product-groups').then(setGroups).catch(() => setGroups([]));
    api.get('/api/properties').then(setProperties).catch(() => setProperties([]));
  }, []);

  const chislo = (v) => (String(v).trim() === '' ? null : Number(String(v).replace(',', '.')));
  const tekst = (v) => (String(v ?? '').trim() === '' ? null : String(v).trim());

  async function create() {
    if (!groupId || !title.trim()) { setError('Иска се група и наименование'); return; }
    setBusy(true);
    setError(null);
    try {
      const p = await api.post('/api/products', {
        groupId: Number(groupId),
        name: title.trim(),
        code: tekst(code),
        unit: tekst(unit) ?? 'бр.',
        barcode: tekst(barcode),
        vatRate: chislo(vatRate) ?? 20,
        purchasePrice: chislo(purchasePrice),
        wholesalePrice: chislo(wholesalePrice),
        retailPrice: chislo(retailPrice),
        note: tekst(note),
        properties: properties
          .map((pr) => ({ propertyId: pr.id, value: tekst(props[String(pr.id)]) }))
          .filter((x) => x.value),
      });
      onDone(p);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const pole = (label, el, w) => (
    <label className="field" style={{ flex: w ?? 1, minWidth: 110 }}>{label}{el}</label>
  );

  return (
    <div style={{
      marginTop: 6, padding: 12, border: '1px solid var(--border)',
      borderRadius: 6, background: 'var(--surface-2)',
    }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 160 }}>
          Група
          <GroupPicker
            label="— група —"
            value={groupId}
            valueLabel={groups.find((g) => String(g.id) === String(groupId))?.name}
            onPick={(id) => setGroupId(id)}
            onClear={() => setGroupId('')}
          />
        </div>
        {pole('Наименование', <input value={title} onChange={(e) => setTitle(e.target.value)} />, 3)}
        {pole('Каталожен номер', <input value={code} onChange={(e) => setCode(e.target.value)} />)}
        {pole('Бърз код', <input value={barcode} onChange={(e) => setBarcode(e.target.value)} />)}
        {pole('Мярка', <input value={unit} onChange={(e) => setUnit(e.target.value)} />, 0.5)}
        {pole('ДДС, %', <input type="number" value={vatRate} onChange={(e) => setVatRate(e.target.value)} />, 0.5)}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {pole('Доставна (без ДДС)', <input type="number" step="0.01" min="0" value={purchasePrice}
          onChange={(e) => setPurchasePrice(e.target.value)} />)}
        {pole('Едро (без ДДС)', <input type="number" step="0.01" min="0" value={wholesalePrice}
          onChange={(e) => setWholesalePrice(e.target.value)} />)}
        {pole('Дребно (без ДДС)', <input type="number" step="0.01" min="0" value={retailPrice}
          onChange={(e) => setRetailPrice(e.target.value)} />)}
        {properties.map((pr) => (
          <label key={pr.id} className="field" style={{ flex: 1, minWidth: 110 }}>
            {pr.name}
            {IZBOR_SVOISTVO[pr.name] ? (
              <select value={props[String(pr.id)] ?? ''}
                onChange={(e) => setProps((m) => ({ ...m, [String(pr.id)]: e.target.value }))}>
                <option value="">—</option>
                {IZBOR_SVOISTVO[pr.name].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ) : (
              <input value={props[String(pr.id)] ?? ''}
                onChange={(e) => setProps((m) => ({ ...m, [String(pr.id)]: e.target.value }))} />
            )}
          </label>
        ))}
      </div>
      <label className="field" style={{ marginTop: 8 }}>
        Описание (излиза във фактурата)
        <input value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="напр. Съвместима тонер касета за HP LaserJet …" />
      </label>
      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="primary" onClick={create} disabled={busy}>Създай</button>
        <button onClick={onCancel} disabled={busy}>Откажи</button>
      </div>
    </div>
  );
}

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Цена за полето „Ед. цена без ДДС" — до стотинка, когато е кръгла
 * („3.75"), иначе до 4 знака („3.3333" — изчислена назад от кръгла цена
 * с ДДС, 4,00 €). Закръглена до стотинка, тя би дала 3,996 € с ДДС.
 */
function cenaVPole(x) {
  const n = Math.round((Number(x) + Number.EPSILON) * 10000) / 10000;
  return Math.abs(n * 100 - Math.round(n * 100)) < 1e-6 ? n.toFixed(2) : String(n);
}

/*
 * Редовете с цена — отгоре, подаръците (тонер/чип, вложени в услугата) —
 * отдолу (01.10.2026). Подредбата е и на екрана, и при записа, за да
 * излезе така и на бона, и на фактурата. При доставка редовете остават
 * в реда на фактурата на доставчика.
 */
function podredeni(lines) {
  if (!lines.some((l) => l.isGift)) return lines;
  return [...lines.filter((l) => !l.isGift), ...lines.filter((l) => l.isGift)];
}
